/**
 * ConstructionSection — project-scoped Construction module.
 *
 * Bid Campaigns is live (Supabase via services/bidCampaigns.js): list campaigns →
 * expand → bidders, a bid-leveling table, questions, and Award. Value Engineering
 * and Construction Contracts remain placeholders (later migration slices).
 *
 * Renders in 'home' (root:construction node) and 'rootZoom'.
 *
 * Caches (project-scoped, on ctx; reset on project change):
 *   ctx.bidCampaignCache        — campaigns array        (undefined|null|[])
 *   ctx.bidCampaignDetailCache  — { [campaignId]: detail } (undefined|null|{...})
 */

import { useState, useEffect, useReducer, useRef } from 'react'
import { createPortal } from 'react-dom'
import { wf, saveViewState } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import ThreadNode from './ThreadNode.jsx'
import { getThreadById, deleteThreadCascade, getProjectThreads } from '../services/threads.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import {
  getProjectCampaigns,
  getCampaignDetail,
  getBidDetail,
  updateCampaign,
  deleteCampaign,
  awardCampaign,
  deleteBid,
  deleteQuestion,
  bidAdjustment,
  CAMPAIGN_STATUS_LABELS,
  getProjectContracts,
  deleteContract,
  CONTRACT_STATUS_LABELS,
  ACTIVE_CONTRACT_CONFLICT,
} from '../services/bidCampaigns.js'
import { getContractRfis, getRfi, deleteRfi, markRfiSeen, RFI_STATUS_LABELS, rfiTurnaround } from '../services/rfis.js'
import { getConcepts } from '../services/design.js'
import { getContractSubmittals, getSpecSectionSubmittals, getSubmittal, getSubmittalRevisions, updateSubmittal, deleteSubmittal, markSubmittalSeen, SUBMITTAL_STATUS_LABELS, submittalTurnaround, submittalLabel } from '../services/submittals.js'
import { generateSubmittalTransmittal } from '../services/pdf.js'
import { RESUBMIT_DISPOSITIONS } from './LogSubmittalReviewModal.jsx'
import { getRevisionFiles, uploadRevisionFile, deleteRevisionFile, getSubmittalFiles, uploadSubmittalFile } from '../services/submittalFiles.js'
import {
  getProjectSpecSections, getConceptSpecSections, createSpecSection, updateSpecSection, deleteSpecSection,
  createSpecSectionVersion, updateSpecSectionVersion,
  getSpecSection, getSpecSectionVersions, getSpecSectionRegisterItems,
  createRegisterItem, updateRegisterItem, deleteRegisterItem, fulfillRegisterItem,
  SPEC_SECTION_STATUS_LABELS, REGISTER_ITEM_STATUS_LABELS, REGISTER_ITEM_TYPES,
  // Round 18 — milestone membership (sheet model)
  getSpecSectionsWithVersions, addSectionToMilestone,
} from '../services/specSections.js'
import { SPEC_RELEASE_MS_TYPE_IDS } from '../outline/constants.js'
import { getContractAsis, getAsi, deleteAsi, issueAsi, rescindAsi, generateAsiPdf, previewAsiPdf, updateAsi, getAsiUploadUrl, ASI_STATUS_LABELS } from '../services/asis.js'
import { getContractProposalRequests, deleteProposalRequest, getProposalRequest, markResponsesSeen } from '../services/proposalRequests.js'
import { confirmDialog, promptDialog, alertDialog, choiceDialog } from './Dialog.jsx'
import { getTeamMembers } from '../services/members.js'
import { MilestoneRow } from './ScheduleSection.jsx'
import { postMilestone } from '../services/milestones.js'
import Markdown from './Markdown.jsx'
import {
  getProjectEfforts,
  deleteEffort,
  deleteItem,
  VE_STATUS_LABELS,
  VE_DISPOSITION_LABELS,
  sumEstimatedSavings,
  sumAcceptedSavings,
  revisedEstimate,
} from '../services/valueEngineering.js'

/* ── Helpers ─────────────────────────────────────────────────────────────── */

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
function fmtDate(d) {
  const p = (d || '').split('-')
  if (p.length === 3) return MONTHS[parseInt(p[1]) - 1] + ' ' + parseInt(p[2])
  return d || ''
}
function money(n) {
  if (n == null || n === '') return '—'
  return '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })
}
function statusColor(status) {
  if (status === 'open')   return '#1D9E75'
  if (status === 'closed') return 'var(--aj-text-muted)'
  return '#2563eb' // preview
}

function loadingRow(text) {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>{text}</span>
        </div>
      </div>
    </div>
  )
}

/* Resolve a CA item (RFI/Submittal) by id from the contract-keyed cache, falling back to
   the thread-mount cache. RFIs/Submittals reached via Schedule > Threads are loaded by
   ThreadCaMount into ctx.threadCaItemCache (keyed by threadId), never into the contract
   cache — so a zoom/breadcrumb/title lookup that only checks the contract cache misses them.
   We do NOT merge into the contract cache: that would make RfiList/SubmittalList think the
   contract's list is fully loaded and hide its other items. */
function findCaItemById(cache, id, kind) {
  if (!id) return null
  for (const cid of Object.keys(cache || {})) {
    const list = cache[cid]
    if (Array.isArray(list)) { const f = list.find(x => x.id === id); if (f) return f }
  }
  const tc = ctx.threadCaItemCache || {}
  for (const tid of Object.keys(tc)) {
    const e = tc[tid]
    if (e && e.kind === kind && e.item && e.item.id === id) return e.item
  }
  // Deep-link cache (populated by the targeted loader when a list cache was never built —
  // e.g. a hard refresh straight onto an entity zoom). Stored as the record, or false/null.
  const z = ctx.caZoomCache && ctx.caZoomCache[id]
  if (z && typeof z === 'object' && z.id === id) return z
  return null
}

/* ── Visibility ─────────────────────────────────────────────────────────── */

function calcConstructionVis() {
  if (!wf.zoomProjectId) return 'hidden'
  const zs = wf.zoomSection || ''
  if (zs.startsWith('bid-detail:')) return 'bidZoom'
  if (zs.startsWith('campaign-detail:')) return 'campaignZoom'
  if (zs.startsWith('ve-effort:')) return 'veEffortZoom'
  if (zs.startsWith('ve-item:')) return 'veItemZoom'
  if (zs.startsWith('contract-detail:')) return 'contractZoom'
  if (zs.startsWith('rfi-detail:')) return 'rfiZoom'
  if (zs.startsWith('submittal-detail:')) return 'submittalZoom'
  if (zs.startsWith('asi-detail:')) return 'asiZoom'
  if (zs.startsWith('spec-section-detail:')) return 'specSectionZoom'
  if (zs.startsWith('proposal-request-detail:')) return 'prZoom'
  if (zs.startsWith('sec:contract:')) return 'contractSectionZoom'
  if (zs.startsWith('sec:construction:')) return 'sectionZoom'
  if (wf.zoomRoot === 'construction' && !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomSection &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery) return 'rootZoom'
  if (wf.zoomRoot === 'reference' && !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomSection &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery) return 'referenceZoom'
  if (!wf.zoomRoot && !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomSection &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery) return 'home'
  return 'hidden'
}

/* ── Bid helpers ────────────────────────────────────────────────────────── */

function bidLowId(bids) {
  if (!bids || !bids.length) return null
  return bids.reduce((m, b) => (Number(b.price_normalized) < Number(m.price_normalized) ? b : m), bids[0]).id
}

// Normalization adjustment fields (label + key) — shown as child rows on a bid.
const BID_ADJ_FIELDS = [
  ['allowance_baseline_delta',     'Allowance baseline'],
  ['alternate_selection_delta',    'Alternate selection'],
  ['excluded_scope_delta',         'Excluded scope'],
  ['bidder_included_extra_deduct', 'Bidder-included extra'],
  ['tax_permits_delta',            'Tax + permits'],
  ['gc_bonding_delta',             'GC + bonding'],
]
const BID_DIVISIONS = ['division_01', 'division_02', 'division_03', 'division_04', 'division_05']

/* A leaf detail row (label · value) used inside an expanded bid. For SHORT values
   only (dates, prices, single words) — the value sits inline in the meta slot. */
function leafRow(label, value, opts = {}) {
  return (
    <div className="wf-node wf-node--item" key={opts.key || label}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item"></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text">{label}</span>
          <span className="wf-meta">
            <span className="wf-node-text" style={opts.color ? { color: opts.color } : undefined}>{value}</span>
          </span>
        </div>
      </div>
    </div>
  )
}

/* A leaf row for LONG text (questions, responses, descriptions, notes): a compact
   uppercase label on its own line, then the full text wrapping below — same treatment
   as the permit activity Remarks. Prevents the single-line overflow/overlap. */
function leafTextRow(label, text, opts = {}) {
  return (
    <div className="wf-node wf-node--item" key={opts.key || label}>
      <div className="wf-row" style={{ alignItems: 'flex-start' }}>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ marginTop: 6 }}></span>
        <div className="wf-text-wrap" style={{ flexDirection: 'column', alignItems: 'stretch', minWidth: 0 }}>
          <span className="wf-node-text">{label}</span>
          {opts.markdown ? (
            <div className="wf-node-text" style={{ color: opts.color, whiteSpace: 'normal', marginTop: 2 }}>
              <Markdown text={text} />
            </div>
          ) : (
            <div className="wf-node-text" style={{ color: opts.color, whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word', marginTop: 2 }}>
              {text}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* A collapsible grouping branch inside an expanded detail (e.g. RFI Info / Request / Response,
   Contract Info). Default-OPEN — these are the content a user wants visible the moment they expand
   the parent, mirroring the reference layouts. (Deliberate exception to the collapsed-by-default rule;
   still drives wf.collapsed so Expand/Collapse all works.) `meta` shows as trailing DM Mono text. */
function DetailBranch({ nodeKey, title, meta, children }) {
  const collapsed = wf.collapsed[nodeKey] === true   // open unless explicitly collapsed
  function toggle(e) { e.stopPropagation(); wf.collapsed[nodeKey] = !collapsed; window.dispatchEvent(new Event('outline-render')) }
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={nodeKey}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <span className="wf-bullet wf-bullet--section"></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{title}</span>
          {meta && <span className="wf-meta"><span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)' }}>{meta}</span></span>}
        </div>
      </div>
      {!collapsed && <div className="wf-children">{children}</div>}
    </div>
  )
}

/* Attachment link rows for a jsonb [{ filename, url }] list, or a single "None" leaf when empty. */
function attachmentRows(atts, keyPrefix) {
  const list = Array.isArray(atts) ? atts.filter(a => a && a.url) : []
  if (!list.length) return leafRow('Attachments', 'None', { key: keyPrefix + '-none', color: 'var(--aj-text-muted)' })
  return list.map((a, i) => (
    <div className="wf-node wf-node--item" key={keyPrefix + '-' + i}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item"></span>
        <div className="wf-text-wrap">
          <a className="wf-node-text" href={a.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: '#2563eb', textDecoration: 'none' }}>📎 {a.filename || 'Attachment'}</a>
        </div>
      </div>
    </div>
  ))
}

/* ── Bid detail rows (children-only) — raw, adjustments, divisions, line items.
   Shared by the expanded BidNode and the bid-detail zoom. ─────────────────── */

function BidDetailRows({ bid }) {
  const lineItems = ctx.bidLineItemCache?.[bid.id]  // undefined|null|[]

  useEffect(() => {
    if (lineItems !== undefined) return
    if (!ctx.bidLineItemCache) ctx.bidLineItemCache = {}
    ctx.bidLineItemCache[bid.id] = null
    getBidDetail(bid.id).then(d => {
      if (!ctx.bidLineItemCache) ctx.bidLineItemCache = {}
      ctx.bidLineItemCache[bid.id] = d.lineItems || []
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {
      if (!ctx.bidLineItemCache) ctx.bidLineItemCache = {}
      ctx.bidLineItemCache[bid.id] = []
      window.dispatchEvent(new Event('outline-render'))
    })
  }, [bid.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const adj = bidAdjustment(bid)
  const rows = []
  rows.push(leafRow('Raw price', money(bid.price_raw), { key: 'raw' }))
  BID_ADJ_FIELDS.forEach(([k, label]) => {
    const v = Number(bid[k] || 0)
    if (v) rows.push(leafRow(label, money(v), { key: k, color: '#d97706' }))
  })
  if (adj) rows.push(leafRow('Total adjustment', money(adj), { key: 'adj', color: '#d97706' }))
  rows.push(leafRow('Normalized price', money(bid.price_normalized), { key: 'norm' }))
  if (bid.contracting_method) rows.push(leafRow('Method', bid.contracting_method, { key: 'method' }))
  if (bid.date_received) rows.push(leafRow('Received', fmtDate(bid.date_received), { key: 'recv' }))
  BID_DIVISIONS.forEach((k, i) => {
    if (bid[k] != null && bid[k] !== '') rows.push(leafRow('Division 0' + (i + 1), money(bid[k]), { key: k }))
  })
  if (Array.isArray(lineItems) && lineItems.length) {
    lineItems.forEach(li => rows.push(leafRow(li.name || li.section_code || 'Line item', money(li.amount), { key: 'li-' + li.id })))
  } else if (lineItems === null) {
    rows.push(leafRow('Line items', 'Loading…', { key: 'li-loading', color: 'var(--aj-text-muted)' }))
  }
  return <>{rows}</>
}

/* ── Bid node (outline row — replaces the old inline leveling table) ──────── */

function BidNode({ campaign, bid, detail, onAward, onChanged }) {
  const key = 'bid:' + bid.id
  const isCollapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  const isLow = bid.id === bidLowId(detail.bids || [])
  const isAwarded = bid.id === (detail.contract?.winning_bid_id || null)
  const hasContract = !!detail.contract

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }
  function zoomIn(e) {
    e.stopPropagation()
    ctx.zoomToSection && ctx.zoomToSection('bid-detail:' + bid.id, { root: 'construction' })
  }
  function edit() {
    window.dispatchEvent(new CustomEvent('open-add-bid-modal', { detail: { campaignId: campaign.id, bidId: bid.id } }))
  }
  async function del() {
    if (!(await confirmDialog({ title: 'Delete bid?', message: 'Delete ' + (bid.bidder_name || 'this bid') + '?', confirmLabel: 'Delete bid', danger: true }))) return
    deleteBid(bid.id).then(() => {
      if (ctx.bidCampaignDetailCache) ctx.bidCampaignDetailCache[campaign.id] = undefined
      ctx.bidCampaignCache = undefined
      onChanged && onChanged()
      window.dispatchEvent(new Event('outline-render'))
    }).catch(e => alertDialog({ title: 'Delete failed', message: e.message || String(e) }))
  }

  return (
    <div className={`wf-node wf-node--item${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--item" title="Zoom into bid" data-kbzoom="1" onClick={zoomIn}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text">{bid.bidder_name || 'Bidder'}</span>
          <span className="wf-meta">
            {isLow && <span style={{ fontSize: 9, color: '#2563eb', fontFamily: '"DM Mono",monospace' }}>LOW</span>}
            {isAwarded && <span style={{ fontSize: 9, color: '#1D9E75', fontFamily: '"DM Mono",monospace' }}>AWARDED</span>}
            <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, fontWeight: 600 }}>{money(bid.price_normalized)}</span>
            {bid.contracting_method && <span style={{ fontSize: 10, color: 'var(--aj-text-muted)' }}>{bid.contracting_method}</span>}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={edit}>Edit bid</ActionMenuItem>
        {!hasContract && <ActionMenuItem onClick={() => onAward(campaign, bid)}>Award</ActionMenuItem>}
        <ActionMenuDivider />
        <ActionMenuItem onClick={del} danger>Delete bid</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          <BidDetailRows bid={bid} />
        </div>
      )}
    </div>
  )
}

/* ── Campaign body (shared by node + zoom — children-only inner content) ──── */

function CampaignBody({ campaign, projectId, onChanged, zoom }) {
  const detail = ctx.bidCampaignDetailCache?.[campaign.id]  // undefined|null|{...}

  function fetchDetail() {
    if (!ctx.bidCampaignDetailCache) ctx.bidCampaignDetailCache = {}
    ctx.bidCampaignDetailCache[campaign.id] = null
    getCampaignDetail(campaign.id).then(d => {
      if (!ctx.bidCampaignDetailCache) ctx.bidCampaignDetailCache = {}
      ctx.bidCampaignDetailCache[campaign.id] = d
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {
      if (!ctx.bidCampaignDetailCache) ctx.bidCampaignDetailCache = {}
      ctx.bidCampaignDetailCache[campaign.id] = { error: true }
      window.dispatchEvent(new Event('outline-render'))
    })
  }

  // Body only mounts when its parent is expanded (or in zoom) — fetch whenever
  // the cache is empty. Re-fires after a bust (cache → undefined + outline-render).
  useEffect(() => {
    if (detail === undefined) fetchDetail()
  }, [detail, campaign.id]) // eslint-disable-line react-hooks/exhaustive-deps

  function bust() {
    if (ctx.bidCampaignDetailCache) ctx.bidCampaignDetailCache[campaign.id] = undefined
    ctx.bidCampaignCache = undefined
    onChanged && onChanged()
    window.dispatchEvent(new Event('outline-render'))
  }
  async function handleAward(camp, bid) {
    const ok = await confirmDialog({ title: 'Award contract?', message: 'Award ' + (bid.bidder_name || 'this bidder') + ' the contract for ' + money(bid.price_normalized) + '?', confirmLabel: 'Award' })
    if (!ok) return
    const fields = {
      project_id:             projectId,
      contractor_airtable_id: bid.bidder_airtable_id || null,
      contractor_name:        bid.bidder_name || null,
      contract_type:          bid.contracting_method === 'Cost Plus' ? 'Cost-Plus' : 'Lump Sum',
      contract_value:         bid.price_normalized != null ? Number(bid.price_normalized) : null,
      winning_bid_id:         bid.id,
    }
    try {
      try {
        await awardCampaign(camp.id, fields)
      } catch (err) {
        // A project has at most one active contract (database/209) — an award makes the new
        // contract active, so awarding over a live one is a real decision, not an error.
        if (err.code !== ACTIVE_CONTRACT_CONFLICT) throw err
        const other = err.conflict?.contractorName || 'the existing contract'
        const ok2 = await confirmDialog({
          title: 'Another contract is already active',
          message: `${other} is the active contract on this project, and a project can only have one at a time. `
            + `Award this contract anyway? ${other} will be marked Closed Out.`,
          confirmLabel: 'Award and close out',
        })
        if (!ok2) return
        await awardCampaign(camp.id, fields, { makeActive: true })
      }
      // The award is the last step to BUILD READY (docs/ALDERFLOW.md §5c2): it posts the project's Bid
      // Campaign milestone, dated today, so the target can see the project reach Build Ready.
      try {
        const bidMs = ((ctx.state && ctx.state.milestones) || []).find(m => !m.fields['Posted Date'] && /^bid campaign/i.test(String((m.fields['Name (from Deliverable)'] || [])[0] || m.fields['Name'] || '')))
        if (bidMs) {
          const d = new Date(); const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
          await postMilestone(bidMs.id, { date: today })
          bidMs.fields['Posted Date'] = today
        }
      } catch (e) { console.warn('[award] Bid Campaign milestone not posted:', e && e.message) }
      if (ctx.bidCampaignDetailCache) ctx.bidCampaignDetailCache[camp.id] = undefined
      ctx.bidCampaignCache = undefined
      onChanged && onChanged()
      window.dispatchEvent(new Event('outline-render'))
    } catch (err) {
      alertDialog({ title: 'Award failed', message: err.message || String(err) })
    }
  }

  if (detail === null || detail === undefined) return loadingRow('Loading…')
  if (detail.error) return loadingRow('Could not load campaign.')

  return (
    <>
      {/* Bidders line */}
      {detail.bidders?.length > 0 && (
        <div style={{ padding: '2px 0 6px 49px', fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)' }}>
          <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginRight: 6 }}>Invited</span>
          {detail.bidders.filter(b => b.role === 'invited').map(b => b.org_name || 'Bidder').join(', ') || '—'}
        </div>
      )}

      {/* Bids — outline rows (sorted by normalized price ascending). The side-by-side
         comparison table lives in the "Compare bids" modal, not inline (outline-first rule). */}
      {(!detail.bids || detail.bids.length === 0) && loadingRow('No bids received yet.')}
      {Array.isArray(detail.bids) && detail.bids.length > 0 && (
        <>
          {[...detail.bids]
            .sort((a, b) => Number(a.price_normalized) - Number(b.price_normalized))
            .map(b => (
              <BidNode key={b.id} campaign={campaign} bid={b} detail={detail} onAward={handleAward} onChanged={onChanged} />
            ))}
          <div className="wf-node wf-node--item">
            <div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
              <div className="wf-text-wrap">
                <button
                  onClick={e => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('open-bid-leveling-modal', { detail: { campaignId: campaign.id } })) }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontFamily: '"DM Sans",sans-serif', fontSize: 11, padding: 0 }}>
                  Compare bids (leveling table) ↗
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Awarded contract */}
      {detail.contract && (
        <div style={{ padding: '2px 0 8px 49px', fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text)' }}>
          <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#1D9E75', marginRight: 6 }}>Contract</span>
          {detail.contract.contractor_name || 'GC'} · {detail.contract.contract_type || '—'} · {money(detail.contract.contract_value)}
        </div>
      )}

      {/* Questions */}
      {detail.questions?.length > 0 && (
        <div style={{ padding: '2px 0 8px 49px' }}>
          <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginBottom: 4 }}>
            Bidder Questions ({detail.questions.length})
          </div>
          {detail.questions.map(q => (
            <div key={q.id} style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', marginBottom: 3 }}>
              <span style={{ flex: 1 }}>
                • {q.question || ''}{q.answer ? <span style={{ color: '#1D9E75' }}> — {q.answer}</span> : <span style={{ color: '#d97706' }}> — unanswered</span>}
              </span>
              <button onClick={() => window.dispatchEvent(new CustomEvent('open-add-bid-question-modal', { detail: { campaignId: campaign.id, questionId: q.id, mode: 'answer', prefill: { question: q.question, answer: q.answer } } }))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: 10, fontFamily: '"DM Sans",sans-serif', padding: 0 }}>{q.answer ? 'edit' : 'answer'}</button>
              <button onClick={async () => { if (await confirmDialog({ title: 'Delete question?', message: 'Delete this question?', confirmLabel: 'Delete', danger: true })) deleteQuestion(q.id).then(bust).catch(e => alertDialog({ title: 'Delete failed', message: e.message || String(e) })) }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 13, padding: 0, lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

/* ── Campaign menu (shared action menu — used by node row and zoom title) ─── */

function buildCampaignMenuItems(campaign, onChanged) {
  function bust() {
    if (ctx.bidCampaignDetailCache) ctx.bidCampaignDetailCache[campaign.id] = undefined
    ctx.bidCampaignCache = undefined
    onChanged && onChanged()
    window.dispatchEvent(new Event('outline-render'))
  }
  function handleAddBid() {
    window.dispatchEvent(new CustomEvent('open-add-bid-modal', { detail: { campaignId: campaign.id, campaignStatus: campaign.status } }))
  }
  function handleAddQuestion() {
    window.dispatchEvent(new CustomEvent('open-add-bid-question-modal', { detail: { campaignId: campaign.id } }))
  }
  function handleEditCampaign() {
    window.dispatchEvent(new CustomEvent('open-add-bid-campaign-modal', { detail: {
      campaignId: campaign.id,
      prefill: {
        round_number: campaign.round_number, status: campaign.status,
        invitation_date: campaign.invitation_date, bid_due_date: campaign.bid_due_date,
        build_start_target_date: campaign.build_start_target_date, pre_bid_job_walk: campaign.pre_bid_job_walk,
        permit_disposition: campaign.permit_disposition,
      },
    } }))
  }
  function handleSetStatus(status) {
    updateCampaign(campaign.id, { status }).then(bust).catch(e => alertDialog({ title: 'Update failed', message: e.message || String(e) }))
  }
  async function handleDeleteCampaign() {
    if (!(await confirmDialog({ title: 'Delete campaign?', message: 'Delete Bidding Campaign No. ' + (campaign.round_number ?? '') + ' and its bids/questions?', confirmLabel: 'Delete campaign', danger: true }))) return
    deleteCampaign(campaign.id).then(bust).catch(e => alertDialog({ title: 'Delete failed', message: e.message || String(e) }))
  }
  function handleCompareBids() {
    window.dispatchEvent(new CustomEvent('open-bid-leveling-modal', { detail: { campaignId: campaign.id } }))
  }
  return (
    <>
      <ActionMenuItem onClick={handleAddBid}>Add bid</ActionMenuItem>
      <ActionMenuItem onClick={handleCompareBids}>Compare bids</ActionMenuItem>
      <ActionMenuItem onClick={handleAddQuestion}>Add question</ActionMenuItem>
      <ActionMenuItem onClick={handleEditCampaign}>Edit campaign</ActionMenuItem>
      <ActionMenuDivider />
      {campaign.status !== 'open' && <ActionMenuItem onClick={() => handleSetStatus('open')}>Open bidding</ActionMenuItem>}
      {campaign.status !== 'closed' && <ActionMenuItem onClick={() => handleSetStatus('closed')}>Close bidding</ActionMenuItem>}
      {campaign.status !== 'preview' && <ActionMenuItem onClick={() => handleSetStatus('preview')}>Set to preview</ActionMenuItem>}
      <ActionMenuDivider />
      <ActionMenuItem onClick={handleDeleteCampaign} danger>Delete campaign</ActionMenuItem>
    </>
  )
}

/* ── Campaign node ──────────────────────────────────────────────────────── */

function CampaignNode({ campaign, projectId, onChanged }) {
  const key = 'bidcamp:' + campaign.id
  const isCollapsed = wf.collapsed[key] !== false
  const detail = ctx.bidCampaignDetailCache?.[campaign.id]  // undefined|null|{...}
  const menu = useActionMenu()

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }
  function zoomIntoCampaign(e) {
    e.stopPropagation()
    ctx.zoomToSection && ctx.zoomToSection('campaign-detail:' + campaign.id, { root: 'construction' })
  }

  const bidCount = detail?.bids?.length
  const sc = statusColor(campaign.status)

  return (
    <div className={`wf-node wf-node--phase${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--phase" title="Zoom into campaign" data-kbzoom="1" onClick={zoomIntoCampaign}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ fontWeight: 600 }}>
            Bidding Campaign No. {campaign.round_number ?? '—'}
          </span>
          <span className="wf-meta">
            <span className="wf-status-pill" style={{ color: sc, borderColor: sc + '33' }}>
              {CAMPAIGN_STATUS_LABELS[campaign.status] || campaign.status}
            </span>
            {campaign.contract_awarded || (detail && detail.contract) ? (
              <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: '#1D9E75' }}>awarded</span>
            ) : null}
            {campaign.bid_due_date && (
              <span className="wf-date-btn" style={{ pointerEvents: 'none', color: 'var(--aj-text-muted)' }}>due {fmtDate(campaign.bid_due_date)}</span>
            )}
            {typeof bidCount === 'number' && (
              <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)' }}>{bidCount} bid{bidCount !== 1 ? 's' : ''}</span>
            )}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        {buildCampaignMenuItems(campaign, onChanged)}
      </ActionMenu>

      {!isCollapsed && (
        <div className="wf-children">
          <CampaignBody campaign={campaign} projectId={projectId} onChanged={onChanged} />
        </div>
      )}
    </div>
  )
}

/* ── Bid Campaigns subsection ───────────────────────────────────────────── */

// Inner content (children-only) — reused by the section node and the section zoom.
function BidCampaignsContent({ projectId, reload }) {
  const campaigns = ctx.bidCampaignCache
  return (
    <>
      {(campaigns === null || campaigns === undefined) && loadingRow('Loading…')}
      {Array.isArray(campaigns) && campaigns.length === 0 && loadingRow('No bid campaigns yet.')}
      {Array.isArray(campaigns) && campaigns.map(c => (
        <CampaignNode key={c.id} campaign={c} projectId={projectId} onChanged={reload} />
      ))}
    </>
  )
}

function BidCampaignsSubSection({ projectId, reload }) {
  const key = 'sec:construction:bid'
  const collapsed = wf.collapsed[key] !== false
  const campaigns = ctx.bidCampaignCache
  const menu = useActionMenu()

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !collapsed
    window.dispatchEvent(new Event('outline-render'))
  }
  function zoomIntoSection(e) {
    e.stopPropagation()
    ctx.zoomToSection && ctx.zoomToSection(key, { root: 'construction' })
  }
  function addCampaign() {
    window.dispatchEvent(new CustomEvent('open-add-bid-campaign-modal', { detail: { projectId: projectId || ctx.recordId || wf.zoomProjectId } }))
  }
  function setAll(expand) {
    if (ctx.expandCollapseDescendants) ctx.expandCollapseDescendants(key, expand)
    window.dispatchEvent(new Event('outline-render'))
  }

  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Zoom into Bid Campaigns" data-kbzoom="1" onClick={zoomIntoSection}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Bid Campaigns</span>
          {Array.isArray(campaigns) && (
            <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{campaigns.length}</span>
          )}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={addCampaign}>Add bid campaign</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => setAll(true)}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={() => setAll(false)}>Collapse all</ActionMenuItem>
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          <BidCampaignsContent projectId={projectId} reload={reload} />
        </div>
      )}
    </div>
  )
}

/* ── Placeholder subsection (later slices) ──────────────────────────────── */

function PlaceholderSubSection({ collapseKey, label }) {
  const collapsed = wf.collapsed[collapseKey] !== false
  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[collapseKey] = !collapsed
    window.dispatchEvent(new Event('outline-render'))
  }
  function zoomIntoSection(e) {
    e.stopPropagation()
    ctx.zoomToSection && ctx.zoomToSection(collapseKey, { root: 'construction' })
  }
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={collapseKey}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title={'Zoom into ' + label} onClick={zoomIntoSection}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{label}</span>
        </div>
      </div>
      {!collapsed && <div className="wf-children">{loadingRow('Coming soon')}</div>}
    </div>
  )
}

/* ── Value Engineering ──────────────────────────────────────────────────── */

function veDispositionColor(d) {
  return d === 'accepted' ? '#1D9E75' : d === 'declined' ? '#c0392b' : d === 'under_review' ? '#d97706' : 'var(--aj-text-muted)'
}

/* VE item detail rows (children-only) — shared by expanded VeItemNode and item zoom. */
function VeItemDetailRows({ item }) {
  const rows = []
  if (item.description)   rows.push(leafTextRow('Proposal', item.description, { key: 'desc' }))
  if (item.design_impact) rows.push(leafTextRow('Design impact', item.design_impact, { key: 'impact' }))
  if (item.notes)         rows.push(leafTextRow('Notes', item.notes, { key: 'notes' }))
  if (!rows.length)       rows.push(leafRow('Proposal', '—', { key: 'empty', color: 'var(--aj-text-muted)' }))
  return <>{rows}</>
}

function VeItemNode({ effort, item, onChanged }) {
  const key = 'veitem:' + item.id
  const isCollapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  const dc = veDispositionColor(item.disposition)

  function toggleCollapse(e) { e.stopPropagation(); wf.collapsed[key] = !isCollapsed; window.dispatchEvent(new Event('outline-render')) }
  function zoomIn(e) { e.stopPropagation(); ctx.zoomToSection && ctx.zoomToSection('ve-item:' + item.id, { root: 'construction' }) }
  function edit() {
    window.dispatchEvent(new CustomEvent('open-add-ve-item-modal', { detail: {
      effortId: effort.id, itemId: item.id,
      prefill: { title: item.title, description: item.description, design_impact: item.design_impact, estimated_savings: item.estimated_savings, notes: item.notes, disposition: item.disposition },
    } }))
  }
  async function del() {
    if (!(await confirmDialog({ title: 'Delete VE item?', message: 'Delete VE item "' + (item.title || '') + '"?', confirmLabel: 'Delete item', danger: true }))) return
    deleteItem(item.id).then(() => { ctx.veEffortCache = undefined; onChanged && onChanged(); window.dispatchEvent(new Event('outline-render')) })
      .catch(e => alertDialog({ title: 'Delete failed', message: e.message || String(e) }))
  }

  return (
    <div className={`wf-node wf-node--item${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--item" title="Zoom into item" data-kbzoom="1" onClick={zoomIn}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text">{item.title || 'VE Item'}</span>
          <span className="wf-meta">
            <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, fontWeight: 600 }}>{money(item.estimated_savings)}</span>
            <span className="wf-status-pill" style={{ color: dc, borderColor: dc + '33' }}>{VE_DISPOSITION_LABELS[item.disposition] || item.disposition}</span>
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={edit}>Edit item</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={del} danger>Delete item</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && <div className="wf-children"><VeItemDetailRows item={item} /></div>}
    </div>
  )
}

/* VE effort body (children-only) — savings summary + item rows. Shared by node + zoom. */
function VeEffortBody({ effort, onChanged }) {
  const items = effort.ve_items || []
  const est = sumEstimatedSavings(effort)
  const acc = sumAcceptedSavings(effort)
  const rev = revisedEstimate(effort)
  return (
    <>
      {effort.baseline_estimate != null && leafRow('Baseline estimate', money(effort.baseline_estimate), { key: 'base' })}
      {leafRow('Estimated savings (all)', money(est), { key: 'est', color: '#d97706' })}
      {leafRow('Accepted savings', money(acc), { key: 'acc', color: '#1D9E75' })}
      {rev != null && leafRow('Revised estimate (accepted)', money(rev), { key: 'rev' })}
      {items.length === 0 && loadingRow('No VE items yet.')}
      {items.map(i => <VeItemNode key={i.id} effort={effort} item={i} onChanged={onChanged} />)}
    </>
  )
}

function VeEffortNode({ effort, onChanged }) {
  const key = 'veeffort:' + effort.id
  const isCollapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  const sc = effort.status === 'complete' ? '#1D9E75' : effort.status === 'under_review' ? '#d97706' : '#2563eb'
  const acc = sumAcceptedSavings(effort)

  function toggleCollapse(e) { e.stopPropagation(); wf.collapsed[key] = !isCollapsed; window.dispatchEvent(new Event('outline-render')) }
  function zoomIn(e) { e.stopPropagation(); ctx.zoomToSection && ctx.zoomToSection('ve-effort:' + effort.id, { root: 'construction' }) }
  function addItem() { window.dispatchEvent(new CustomEvent('open-add-ve-item-modal', { detail: { effortId: effort.id } })) }
  function editEffort() {
    window.dispatchEvent(new CustomEvent('open-add-ve-effort-modal', { detail: {
      effortId: effort.id,
      prefill: { effort_number: effort.effort_number, status: effort.status, baseline_estimate: effort.baseline_estimate },
    } }))
  }
  async function del() {
    if (!(await confirmDialog({ title: 'Delete VE effort?', message: 'Delete VE Effort No. ' + (effort.effort_number ?? '') + ' and its items?', confirmLabel: 'Delete effort', danger: true }))) return
    deleteEffort(effort.id).then(() => { ctx.veEffortCache = undefined; onChanged && onChanged(); window.dispatchEvent(new Event('outline-render')) })
      .catch(e => alertDialog({ title: 'Delete failed', message: e.message || String(e) }))
  }

  return (
    <div className={`wf-node wf-node--phase${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--phase" title="Zoom into VE effort" data-kbzoom="1" onClick={zoomIn}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ fontWeight: 600 }}>VE Effort No. {effort.effort_number ?? '—'}</span>
          <span className="wf-meta">
            <span className="wf-status-pill" style={{ color: sc, borderColor: sc + '33' }}>{VE_STATUS_LABELS[effort.status] || effort.status}</span>
            {acc > 0 && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: '#1D9E75' }}>{money(acc)} accepted</span>}
            {Array.isArray(effort.ve_items) && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)' }}>{effort.ve_items.length} item{effort.ve_items.length !== 1 ? 's' : ''}</span>}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={addItem}>Add item</ActionMenuItem>
        <ActionMenuItem onClick={editEffort}>Edit effort</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={del} danger>Delete effort</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && <div className="wf-children"><VeEffortBody effort={effort} onChanged={onChanged} /></div>}
    </div>
  )
}

// Inner content (children-only) — reused by the section node and section zoom.
function VeEffortsContent({ reload }) {
  const efforts = ctx.veEffortCache
  return (
    <>
      {(efforts === null || efforts === undefined) && loadingRow('Loading…')}
      {Array.isArray(efforts) && efforts.length === 0 && loadingRow('No value engineering efforts yet.')}
      {Array.isArray(efforts) && efforts.map(e => <VeEffortNode key={e.id} effort={e} onChanged={reload} />)}
    </>
  )
}

function VeEffortsSubSection({ reload }) {
  const key = 'sec:construction:ve'
  const collapsed = wf.collapsed[key] !== false
  const efforts = ctx.veEffortCache
  const menu = useActionMenu()
  function toggleCollapse(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; window.dispatchEvent(new Event('outline-render')) }
  function zoomIntoSection(e) { e.stopPropagation(); ctx.zoomToSection && ctx.zoomToSection(key, { root: 'construction' }) }
  function addEffort() {
    window.dispatchEvent(new CustomEvent('open-add-ve-effort-modal', { detail: { projectId: ctx.recordId || wf.zoomProjectId } }))
  }
  function setAll(expand) {
    if (ctx.expandCollapseDescendants) ctx.expandCollapseDescendants(key, expand)
    window.dispatchEvent(new Event('outline-render'))
  }
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Zoom into Value Engineering" data-kbzoom="1" onClick={zoomIntoSection}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Value Engineering</span>
          {Array.isArray(efforts) && <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{efforts.length}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={addEffort}>Add VE effort</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => setAll(true)}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={() => setAll(false)}>Collapse all</ActionMenuItem>
      </ActionMenu>
      {!collapsed && <div className="wf-children"><VeEffortsContent reload={reload} /></div>}
    </div>
  )
}

/* ── Construction Contracts ─────────────────────────────────────────────── */

function contractStatusColor(s) {
  return s === 'closed' ? 'var(--aj-text-muted)' : s === 'on_hold' ? '#d97706' : '#1D9E75'
}

/* Contract detail rows (children-only) — shared by node body + zoom. */
function ContractBody({ contract, onChanged }) {
  // Contract metadata is consolidated into its own "Contract Info" branch so the RFIs / Submittals /
  // ASIs / Proposal Requests sit as clean siblings beneath the contract (per the reference layout).
  const info = []
  if (contract.contractor_name) info.push(leafRow('Contractor', contract.contractor_name, { key: 'gc' }))
  if (contract.contract_type)   info.push(leafRow('Type', contract.contract_type, { key: 'type' }))
  info.push(leafRow('Value', money(contract.contract_value), { key: 'val' }))
  if (contract.contract_start_date) info.push(leafRow('Contract start', fmtDate(contract.contract_start_date), { key: 'start' }))
  if (contract.build_start_date) info.push(leafRow('Build start', fmtDate(contract.build_start_date), { key: 'build' }))
  if (contract.substantial_completion_date) info.push(leafRow('Substantial completion', fmtDate(contract.substantial_completion_date), { key: 'sub' }))
  if (contract.notes) info.push(leafTextRow('Notes', contract.notes, { key: 'notes' }))
  return (
    <>
      <DetailBranch nodeKey={'contract:' + contract.id + ':info'} title="Contract Info">{info}</DetailBranch>
      <RfiList contract={contract} onChanged={onChanged} />
      <SubmittalList contract={contract} onChanged={onChanged} />
      <AsiList contract={contract} onChanged={onChanged} />
      <ProposalRequestList contract={contract} onChanged={onChanged} />
    </>
  )
}

function ContractNode({ contract, onChanged }) {
  const key = 'contract:' + contract.id
  const isCollapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  const sc = contractStatusColor(contract.status)

  function toggleCollapse(e) { e.stopPropagation(); wf.collapsed[key] = !isCollapsed; window.dispatchEvent(new Event('outline-render')) }
  function zoomIn(e) { e.stopPropagation(); ctx.zoomToSection && ctx.zoomToSection('contract-detail:' + contract.id, { root: 'construction' }) }
  function edit() {
    window.dispatchEvent(new CustomEvent('open-add-contract-modal', { detail: {
      contractId: contract.id,
      prefill: {
        contractor_name: contract.contractor_name, contract_type: contract.contract_type,
        contract_value: contract.contract_value, contract_start_date: contract.contract_start_date,
        build_start_date: contract.build_start_date, substantial_completion_date: contract.substantial_completion_date,
        status: contract.status, notes: contract.notes,
      },
    } }))
  }
  async function del() {
    if (!(await confirmDialog({ title: 'Delete contract?', message: 'Delete the contract with ' + (contract.contractor_name || 'this GC') + '?', confirmLabel: 'Delete contract', danger: true }))) return
    deleteContract(contract.id).then(() => { ctx.contractCache = undefined; onChanged && onChanged(); window.dispatchEvent(new Event('outline-render')) })
      .catch(e => alertDialog({ title: 'Delete failed', message: e.message || String(e) }))
  }
  function portalLink() {
    window.dispatchEvent(new CustomEvent('open-contractor-portal-link', { detail: { contractId: contract.id, contractorName: contract.contractor_name } }))
  }

  return (
    <div className={`wf-node wf-node--phase${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--phase" title="Zoom into contract" data-kbzoom="1" onClick={zoomIn}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ fontWeight: 600 }}>{contract.contractor_name || 'Construction Contract'}</span>
          <span className="wf-meta">
            {contract.status && <span className="wf-status-pill" style={{ color: sc, borderColor: sc + '33' }}>{CONTRACT_STATUS_LABELS[contract.status] || contract.status}</span>}
            {contract.contract_type && <span style={{ fontSize: 10, color: 'var(--aj-text-muted)' }}>{contract.contract_type}</span>}
            {contract.contract_value != null && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, fontWeight: 600 }}>{money(contract.contract_value)}</span>}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={edit}>Edit contract</ActionMenuItem>
        <ActionMenuItem onClick={portalLink}>Contractor portal link</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={del} danger>Delete contract</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && <div className="wf-children"><ContractBody contract={contract} onChanged={onChanged} /></div>}
    </div>
  )
}

// Inner content (children-only) — reused by the section node and section zoom.
function ContractsContent({ reload }) {
  const contracts = ctx.contractCache
  return (
    <>
      {(contracts === null || contracts === undefined) && loadingRow('Loading…')}
      {Array.isArray(contracts) && contracts.length === 0 && loadingRow('No contracts yet. Award a bid campaign to create one.')}
      {Array.isArray(contracts) && contracts.map(c => <ContractNode key={c.id} contract={c} onChanged={reload} />)}
    </>
  )
}

/* Directional dialog shown when someone picks "Add contract": contracts are created by
   awarding a bidder in a Bid Campaign, not added manually. */
function AwardContractInfoDialog({ onClose }) {
  function goToCampaigns() {
    onClose()
    ctx.zoomToSection && ctx.zoomToSection('sec:construction:bid', { root: 'construction' })
  }
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 420, padding: '20px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>Add a Construction Contract</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.55, color: 'var(--aj-text-mid)', marginBottom: 16 }}>
          Construction contracts are created by <strong style={{ color: 'var(--aj-text)' }}>awarding a bidder</strong> in a Bid
          Campaign. Open a campaign, review the bids, and award the winning bidder — the contract is
          created automatically and appears here.
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Close</button>
          <button onClick={goToCampaigns} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: '#07272D', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Open Bid Campaigns</button>
        </div>
      </div>
    </div>,
    document.body
  )
}

function ContractsSubSection({ reload }) {
  const key = 'sec:construction:contracts'
  const collapsed = wf.collapsed[key] !== false
  const contracts = ctx.contractCache
  const menu = useActionMenu()
  const [showAwardInfo, setShowAwardInfo] = useState(false)
  function toggleCollapse(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; window.dispatchEvent(new Event('outline-render')) }
  function zoomIntoSection(e) { e.stopPropagation(); ctx.zoomToSection && ctx.zoomToSection(key, { root: 'construction' }) }
  function setAll(expand) {
    if (ctx.expandCollapseDescendants) ctx.expandCollapseDescendants(key, expand)
    window.dispatchEvent(new Event('outline-render'))
  }
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Zoom into Construction Contracts" data-kbzoom="1" onClick={zoomIntoSection}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Construction Contracts</span>
          {Array.isArray(contracts) && <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{contracts.length}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => setShowAwardInfo(true)}>Add contract</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => setAll(true)}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={() => setAll(false)}>Collapse all</ActionMenuItem>
      </ActionMenu>
      {showAwardInfo && <AwardContractInfoDialog onClose={() => setShowAwardInfo(false)} />}
      {!collapsed && <div className="wf-children"><ContractsContent reload={reload} /></div>}
    </div>
  )
}

/* ── RFIs (first CA sub-module, under a contract) ───────────────────────── */

function rfiStatusColor(s) {
  return s === 'closed' ? 'var(--aj-text-muted)' : s === 'withdrawn' ? '#c0392b' : s === 'Draft Response' ? '#d97706' : '#1D9E75'
}

/* A CA item's linked Cortex thread, rendered inline (children-only) via the canonical
   ThreadNode — which owns status, target deadline, activity log, and Slack.
   Generic across CA types (RFIs, Submittals, …). Lazy-loads the Airtable Thread record
   into ctx.caThreadCache[threadId]. */
function CaThreadMount({ threadId, projectId }) {
  const tid = threadId || ''
  const pid = projectId || ctx.recordId || wf.zoomProjectId || ''
  const projectName = ctx.state?.milestones?.[0]?.fields?.['Project Name'] || ''
  const loadedRef = useRef(false)

  useEffect(() => {
    if (!tid || loadedRef.current) return
    loadedRef.current = true
    if (!ctx.caThreadCache) ctx.caThreadCache = {}
    if (ctx.caThreadCache[tid] !== undefined) return
    ctx.caThreadCache[tid] = null
    getThreadById(tid).then(rec => {
      if (!ctx.caThreadCache) ctx.caThreadCache = {}
      ctx.caThreadCache[tid] = rec || null
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => { if (ctx.caThreadCache) ctx.caThreadCache[tid] = null; window.dispatchEvent(new Event('outline-render')) })
  }, [tid]) // eslint-disable-line react-hooks/exhaustive-deps

  // Reload when the entry is busted to undefined (e.g. a thread edit) — mirrors useCaCacheReload.
  useEffect(() => {
    function onRender() {
      if (!tid || !ctx.caThreadCache || ctx.caThreadCache[tid] !== undefined) return
      ctx.caThreadCache[tid] = null
      getThreadById(tid)
        .then(rec => { ctx.caThreadCache[tid] = rec || null; window.dispatchEvent(new Event('outline-render')) })
        .catch(() => { ctx.caThreadCache[tid] = null; window.dispatchEvent(new Event('outline-render')) })
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [tid]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!tid) return loadingRow('No Cortex thread linked.')
  const thread = ctx.caThreadCache?.[tid]
  if (thread === undefined || thread === null) return loadingRow(thread === null ? 'Loading thread…' : 'Loading…')
  /* hideCaMount breaks the mutual-mount recursion (thread would otherwise
     mount this CA item right back via ThreadCaMount) */
  return <ThreadNode item={thread} projectId={pid} projectName={projectName} hideCaMount labelPrefix="Thread: " />
}

/* An ASI's linked milestone, rendered inline via the canonical MilestoneRow
   (Round 7 — ASIs are architect-driven and run as milestones, not threads).
   The milestone lives in ctx.state.milestones (project schedule data). */
function CaMilestoneMount({ milestoneId, projectId }) {
  if (!milestoneId) return loadingRow('No milestone linked.')
  const ms = (ctx.state?.milestones || []).find(m => m.id === milestoneId)
  if (!ms) return loadingRow('Milestone loading…')
  const pname = ms.fields['Project Phase'] || ''
  const ph = (ctx.state?.phases || []).find(p => (p.fields['Phase Name'] || '') === pname)
  return <MilestoneRow ms={ms} phId={ph ? ph.id : ''} projId={projectId || ctx.recordId || wf.zoomProjectId || ''} labelPrefix="Milestone: " />
}

/* RFI detail rows (children-only) — shared by expanded RfiNode and rfi-detail zoom. */
/* RFI detail, organized into three branches (Info / Request / Response) per the reference layout.
   Request carries the received-from-GC date as meta; Response carries the returned date. */
function RfiDetailRows({ rfi }) {
  const tat = rfiTurnaround(rfi)
  const info = []
  if (Array.isArray(rfi.reviewing_party) && rfi.reviewing_party.length) info.push(leafRow('Reviewing party', rfi.reviewing_party.join(', '), { key: 'rev' }))
  if (rfi.origin)  info.push(leafRow('Origin', rfi.origin, { key: 'origin' }))
  if (tat != null) info.push(leafRow('Review time', tat + ' day' + (tat !== 1 ? 's' : ''), { key: 'tat', color: 'var(--aj-text-muted)' }))
  if (!info.length) info.push(leafRow('Reviewing party', '—', { key: 'noinfo', color: 'var(--aj-text-muted)' }))

  const hasResponse = !!(rfi.response || rfi.date_returned)
  return (
    <>
      <DetailBranch nodeKey={'rfi:' + rfi.id + ':info'} title="Info">{info}</DetailBranch>
      <DetailBranch nodeKey={'rfi:' + rfi.id + ':request'} title="Request" meta={rfi.date_received ? fmtDate(rfi.date_received) : null}>
        {rfi.question
          ? leafTextRow('Question', rfi.question, { key: 'q', markdown: true })
          : leafRow('Question', '—', { key: 'q', color: 'var(--aj-text-muted)' })}
        {attachmentRows(rfi.attachments, 'rfiatt')}
      </DetailBranch>
      {hasResponse && (
        <DetailBranch nodeKey={'rfi:' + rfi.id + ':response'} title="Response" meta={rfi.date_returned ? fmtDate(rfi.date_returned) : null}>
          {rfi.response
            ? leafTextRow('Response', rfi.response, { key: 'r', markdown: true })
            : leafRow('Response', '—', { key: 'r', color: 'var(--aj-text-muted)' })}
        </DetailBranch>
      )}
    </>
  )
}

/* hideThreadMount: set by ThreadCaMount (thread → CA item direction) so the
   RFI doesn't mount the thread right back — breaks the render recursion. */
/* A portal-submitted RFI/Submittal/response stays "new" (team_seen_at null) until a team member
 * opens it — rendered bold with a NEW tag, and counted on its parent node's NEW badge. */
function isUnseen(rec) { return !!rec && !rec.team_seen_at }
function NewPill({ n }) {
  return (
    <span style={{ marginLeft: 8, fontFamily: '"DM Mono",monospace', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', color: '#fff', background: '#c2691c', borderRadius: 3, padding: '1px 5px', textTransform: 'uppercase', verticalAlign: 'middle' }}>
      {n ? n + ' new' : 'New'}
    </span>
  )
}

export function RfiNode({ rfi, contract, onChanged, hideThreadMount, hideMilestoneMount, labelPrefix = '', mountCtx = '' }) {
  // mountCtx disambiguates this rfi's collapse state across its possible mounts (Reference
  // list, MilestoneRfiMount, ThreadCaMount) — see the identical AsiNode fix note above.
  const key = 'rfi:' + rfi.id + (mountCtx ? ':' + mountCtx : '')
  const isCollapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  const sc = rfiStatusColor(rfi.status)
  const tat = rfiTurnaround(rfi)
  const unseen = isUnseen(rfi)

  // Clear the NEW highlight the first time a team member opens (expands or zooms into) this RFI.
  function markSeen() {
    if (!isUnseen(rfi)) return
    rfi.team_seen_at = new Date().toISOString()  // mutate the cached object so the badge updates
    markRfiSeen(rfi.id).catch(() => {})
    window.dispatchEvent(new Event('outline-render'))
  }
  function toggleCollapse(e) { e.stopPropagation(); const opening = isCollapsed; wf.collapsed[key] = !isCollapsed; if (opening) markSeen(); window.dispatchEvent(new Event('outline-render')) }
  function zoomIn(e) { e.stopPropagation(); markSeen(); ctx.zoomToSection && ctx.zoomToSection('rfi-detail:' + rfi.id, { root: 'construction' }) }
  function edit() {
    window.dispatchEvent(new CustomEvent('open-add-rfi-modal', { detail: {
      contractId: contract.id, projectId: rfi.project_id || contract.project_id, rfiId: rfi.id,
      prefill: { rfi_number: rfi.rfi_number, subject: rfi.subject, reviewing_party: rfi.reviewing_party, origin: rfi.origin, question: rfi.question, response: rfi.response, date_received: rfi.date_received, date_returned: rfi.date_returned, status: rfi.status, thread_airtable_id: rfi.thread_airtable_id, milestone_airtable_id: rfi.milestone_airtable_id },
    } }))
  }
  async function del() {
    if (!(await confirmDialog({ title: 'Delete RFI?', message: 'Delete RFI ' + (rfi.rfi_number ?? '') + '?', confirmLabel: 'Delete RFI', danger: true }))) return
    // If the RFI has a linked thread, offer to delete it too (full cleanup: thread + activity
    // log). deleteThreadCascade removes the linked RFI as well, so we don't also call deleteRfi.
    let alsoThread = false
    if (rfi.thread_airtable_id) {
      alsoThread = await confirmDialog({
        title: 'Delete the linked thread too?',
        message: 'This RFI has a linked Thread. Delete it as well — including its full activity log? This can’t be undone.',
        confirmLabel: 'Delete thread too', cancelLabel: 'Keep thread', danger: true,
      })
    }
    const projectId = rfi.project_id || contract.project_id
    const op = alsoThread ? deleteThreadCascade(rfi.thread_airtable_id) : deleteRfi(rfi.id)
    op.then(async () => {
      if (ctx.rfiCache) ctx.rfiCache[contract.id] = undefined
      if (ctx.milestoneRfiCache && rfi.milestone_airtable_id) ctx.milestoneRfiCache[rfi.milestone_airtable_id] = undefined
      if (ctx.threadCaItemCache && rfi.thread_airtable_id) ctx.threadCaItemCache[rfi.thread_airtable_id] = undefined
      if (alsoThread) {
        if (ctx.caThreadCache) delete ctx.caThreadCache[rfi.thread_airtable_id]
        if (projectId) { try { ctx.threadCache = await getProjectThreads(projectId) } catch (_) { /* non-fatal */ } }
      }
      onChanged && onChanged()
      window.dispatchEvent(new Event('outline-render'))
    }).catch(e => alertDialog({ title: 'Delete failed', message: e.message || String(e) }))
  }

  return (
    <div className={`wf-node wf-node--item${isCollapsed ? ' wf-node--collapsed' : ''}${rfi.status === 'closed' ? ' wf-node--is-done' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--item" title="Zoom into RFI" data-kbzoom="1" onClick={zoomIn}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={unseen ? { fontWeight: 700 } : undefined}>
            {labelPrefix}
            {rfi.rfi_number != null && <span style={{ fontFamily: '"DM Mono",monospace', color: 'var(--aj-text-muted)', marginRight: 6 }}>#{rfi.rfi_number}</span>}
            {rfi.subject || 'RFI'}
            {unseen && <NewPill />}
          </span>
          <span className="wf-meta" style={{ marginLeft: 'auto' }}>
            {rfi.date_received && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)' }}>{fmtDate(rfi.date_received)}</span>}
            <span className="wf-status-pill" style={{ color: sc, borderColor: sc + '33' }}>{RFI_STATUS_LABELS[rfi.status] || rfi.status}</span>
            {tat != null && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)' }}>{tat}d</span>}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        {rfi.status !== 'closed' && (
          <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-rfi-respond', { detail: { rfi } }))}>
            {rfi.response ? 'Continue response' : 'Respond'}
          </ActionMenuItem>
        )}
        {rfi.status !== 'closed' && <ActionMenuDivider />}
        <ActionMenuItem onClick={edit}>Edit RFI</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={del} danger>Delete RFI</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          <RfiDetailRows rfi={rfi} />
          {!hideMilestoneMount && (rfi.milestone_airtable_id
            ? <CaMilestoneMount milestoneId={rfi.milestone_airtable_id} projectId={rfi.project_id} />
            : (!hideThreadMount && <CaThreadMount threadId={rfi.thread_airtable_id} projectId={rfi.project_id} />))}
        </div>
      )}
    </div>
  )
}

/* RFIs sub-branch under a contract — lazy-loads ctx.rfiCache[contractId]. */
/* ── Shared reload-on-bust hook for contract-scoped lists ─────────────────
 * The add/edit modals refresh a list by busting its ctx cache entry to
 * `undefined` and dispatching outline-render. The initial load effect only
 * re-runs on collapse/id changes, so without this listener a busted cache
 * strands the expanded list on "Loading…" until it's collapsed and
 * re-expanded. Mirrors the two-effect pattern in ThreadActivitySection
 * (ThreadNode.jsx) and ReviewActivitySection (PermitsSection.jsx).
 */
function useCaCacheReload(cacheName, id, collapsed, fetcher) {
  useEffect(() => {
    function onRender() {
      if (collapsed) return
      if (!ctx[cacheName]) return
      if (ctx[cacheName][id] !== undefined) return // loaded or in-flight
      ctx[cacheName][id] = null
      fetcher(id).then(r => {
        if (!ctx[cacheName]) ctx[cacheName] = {}
        ctx[cacheName][id] = r
        window.dispatchEvent(new Event('outline-render'))
      }).catch(() => {
        // Reset to undefined (not []) on failure — [] would permanently read as "confirmed
        // empty" and stop retrying forever. undefined lets the next outline-render retry.
        if (!ctx[cacheName]) ctx[cacheName] = {}
        ctx[cacheName][id] = undefined
        window.dispatchEvent(new Event('outline-render'))
      })
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [cacheName, id, collapsed]) // eslint-disable-line react-hooks/exhaustive-deps
}

/* ── Proposal Requests (architect-issued) under a contract ────────────────── */

const PR_STATUS_LABELS = { open: 'Open', proposal_received: 'Proposal received', closed: 'Closed', void: 'Void' }
function prStatusColor(s) { return s === 'closed' ? 'var(--aj-text-muted)' : s === 'void' ? '#c0392b' : s === 'proposal_received' ? '#1a6fa8' : '#1D9E75' }

function ProposalRequestBody({ pr }) {
  const rows = []
  if (pr.pr_number) rows.push(leafRow('PR #', pr.pr_number, { key: 'num' }))
  if (pr.issue_date) rows.push(leafRow('Issued', fmtDate(pr.issue_date), { key: 'iss' }))
  if (pr.due_date) rows.push(leafRow('Response due', fmtDate(pr.due_date), { key: 'due' }))
  rows.push(leafRow('Status', PR_STATUS_LABELS[pr.status] || pr.status, { key: 'st', color: prStatusColor(pr.status) }))
  const att = Array.isArray(pr.attachment_list) ? pr.attachment_list : []
  const resps = Array.isArray(pr.responses) ? pr.responses : []
  return (
    <>
      {rows}
      {pr.description && leafTextRow('Description', pr.description, { key: 'desc', markdown: true })}
      {att.map((a, i) => (
        <div key={'att' + i} className="wf-node wf-node--item"><div className="wf-row">
          <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
          <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
          <span className="wf-bullet wf-bullet--item" />
          <div className="wf-text-wrap"><a href={a.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 12, color: '#2563eb', textDecoration: 'none' }}>📎 {a.filename || 'Attachment'} ↗</a></div>
        </div></div>
      ))}
      {resps.map(r => (
        <div key={r.id} className="wf-node wf-node--item"><div className="wf-row" style={{ alignItems: 'flex-start' }}>
          <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
          <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
          <span className="wf-bullet wf-bullet--item" style={{ marginTop: 6 }} />
          <div className="wf-text-wrap" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 2 }}>
            <div style={{ fontSize: 13 }}>
              <span style={{ fontWeight: 600 }}>Proposal</span>
              {isUnseen(r) && <NewPill />}
              {r.amount != null && <span style={{ marginLeft: 8, fontFamily: '"DM Mono",monospace' }}>{money(r.amount)}</span>}
              {r.schedule_impact_days != null && <span style={{ marginLeft: 8, color: 'var(--aj-text-muted)' }}>{r.schedule_impact_days}d schedule impact</span>}
              {r.submitted_by && <span style={{ marginLeft: 8, opacity: 0.55 }}>· {r.submitted_by}</span>}
            </div>
            {r.notes && <div style={{ fontSize: 12.5, color: 'var(--aj-text-mid)', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{r.notes}</div>}
            {Array.isArray(r.files) && r.files.map((f, i) => <a key={i} href={f.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 11.5, color: '#2563eb', textDecoration: 'none' }}>📎 {f.filename} ↗</a>)}
          </div>
        </div></div>
      ))}
    </>
  )
}

function ProposalRequestNode({ pr, contract, onChanged }) {
  const key = 'pr:' + pr.id
  const isCollapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  const sc = prStatusColor(pr.status)
  const unseen = Array.isArray(pr.responses) && pr.responses.some(isUnseen)
  // Clear the NEW highlight on the PR's portal responses once a team member opens it.
  function markSeen() {
    if (!Array.isArray(pr.responses) || !pr.responses.some(isUnseen)) return
    const now = new Date().toISOString()
    pr.responses.forEach(r => { if (isUnseen(r)) r.team_seen_at = now })
    markResponsesSeen(pr.id).catch(() => {})
    window.dispatchEvent(new Event('outline-render'))
  }
  function toggleCollapse(e) { e.stopPropagation(); const opening = isCollapsed; wf.collapsed[key] = !isCollapsed; if (opening) markSeen(); window.dispatchEvent(new Event('outline-render')) }
  function zoomIn(e) { e.stopPropagation(); markSeen(); ctx.zoomToSection && ctx.zoomToSection('proposal-request-detail:' + pr.id, { root: 'construction' }) }
  function edit() {
    window.dispatchEvent(new CustomEvent('open-add-proposal-request-modal', { detail: {
      contractId: contract.id, projectId: pr.project_id || contract.project_id, prId: pr.id,
      prefill: { pr_number: pr.pr_number, subject: pr.subject, description: pr.description, issue_date: pr.issue_date, due_date: pr.due_date, status: pr.status, attachment_list: pr.attachment_list },
    } }))
  }
  async function del() {
    if (!(await confirmDialog({ title: 'Delete proposal request?', message: 'Delete proposal request ' + (pr.pr_number || '') + '?', confirmLabel: 'Delete', danger: true }))) return
    deleteProposalRequest(pr.id).then(() => { if (ctx.proposalRequestCache) ctx.proposalRequestCache[contract.id] = undefined; onChanged && onChanged(); window.dispatchEvent(new Event('outline-render')) })
      .catch(e => alertDialog({ title: 'Delete failed', message: e.message || String(e) }))
  }
  return (
    <div className={`wf-node wf-node--item${isCollapsed ? ' wf-node--collapsed' : ''}${(pr.status === 'closed' || pr.status === 'void') ? ' wf-node--is-done' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}><span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span></button>
        <button className="wf-bullet wf-bullet--item" title="Zoom into proposal request" data-kbzoom="1" onClick={zoomIn}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={unseen ? { fontWeight: 700 } : undefined}>
            {pr.pr_number && <span style={{ fontFamily: '"DM Mono",monospace', color: 'var(--aj-text-muted)', marginRight: 6 }}>{pr.pr_number}</span>}
            {pr.subject || 'Proposal Request'}
            {unseen && <NewPill />}
          </span>
          <span className="wf-meta">
            <span className="wf-status-pill" style={{ color: sc, borderColor: sc + '33' }}>{PR_STATUS_LABELS[pr.status] || pr.status}</span>
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={edit}>Edit proposal request</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={del} danger>Delete proposal request</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && <div className="wf-children"><ProposalRequestBody pr={pr} /></div>}
    </div>
  )
}

function ProposalRequestList({ contract, onChanged, zoom }) {
  const key = 'sec:contract:' + contract.id + ':prs'
  const collapsed = wf.collapsed[key] !== false
  const open = zoom || !collapsed   // zoom always shows children
  const prs = ctx.proposalRequestCache?.[contract.id]
  const menu = useActionMenu()
  useCaCacheReload('proposalRequestCache', contract.id, false, getContractProposalRequests)
  // Eager-load (not gated by collapse) so a directly-submitted GC proposal surfaces its NEW badge.
  useEffect(() => {
    if (prs !== undefined) return
    if (!ctx.proposalRequestCache) ctx.proposalRequestCache = {}
    ctx.proposalRequestCache[contract.id] = null
    getContractProposalRequests(contract.id).then(r => {
      if (!ctx.proposalRequestCache) ctx.proposalRequestCache = {}
      ctx.proposalRequestCache[contract.id] = r
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => { if (ctx.proposalRequestCache) ctx.proposalRequestCache[contract.id] = undefined; window.dispatchEvent(new Event('outline-render')) })
  }, [contract.id]) // eslint-disable-line react-hooks/exhaustive-deps
  function toggleCollapse(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; window.dispatchEvent(new Event('outline-render')) }
  function add() {
    window.dispatchEvent(new CustomEvent('open-add-proposal-request-modal', { detail: { contractId: contract.id, projectId: contract.project_id } }))
  }
  function setAll(expand) {
    wf.collapsed[key] = !expand
    if (Array.isArray(prs)) prs.forEach(p => { wf.collapsed['pr:' + p.id] = !expand })
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }
  function zoomIntoSection(e) { e.stopPropagation(); ctx.zoomToSection && ctx.zoomToSection(key, { root: 'construction' }) }
  const count = Array.isArray(prs) ? prs.length : null
  const unseenCount = Array.isArray(prs) ? prs.filter(p => Array.isArray(p.responses) && p.responses.some(isUnseen)).length : 0
  const childrenContent = (
    <>
      {(prs === null || prs === undefined) && loadingRow('Loading…')}
      {Array.isArray(prs) && prs.length === 0 && loadingRow('No proposal requests yet.')}
      {Array.isArray(prs) && prs.map(p => <ProposalRequestNode key={p.id} pr={p} contract={contract} onChanged={onChanged} />)}
    </>
  )
  if (zoom) return childrenContent   // section-zoom: children only, no header row
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}><span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span></button>
        <button className="wf-bullet wf-bullet--section" title="Zoom into Proposal Requests" data-kbzoom="1" onClick={zoomIntoSection}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Proposal Requests</span>
          {count != null && <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{count}</span>}
          {unseenCount > 0 && <NewPill n={unseenCount} />}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={add}>Add proposal request</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => setAll(true)}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={() => setAll(false)}>Collapse all</ActionMenuItem>
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          {childrenContent}
        </div>
      )}
    </div>
  )
}

function RfiList({ contract, onChanged, zoom }) {
  const key = 'sec:contract:' + contract.id + ':rfis'
  const collapsed = wf.collapsed[key] !== false
  const open = zoom || !collapsed   // zoom always shows children
  const rfis = ctx.rfiCache?.[contract.id]
  const menu = useActionMenu()
  useCaCacheReload('rfiCache', contract.id, false, getContractRfis)

  // Eager-load (not gated by collapse) so the NEW badge + count surface on the collapsed node —
  // a GC's RFI now posts directly here, so the firm needs to see it without expanding first.
  useEffect(() => {
    if (rfis !== undefined) return
    if (!ctx.rfiCache) ctx.rfiCache = {}
    ctx.rfiCache[contract.id] = null
    getContractRfis(contract.id).then(r => {
      if (!ctx.rfiCache) ctx.rfiCache = {}
      ctx.rfiCache[contract.id] = r
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => { if (ctx.rfiCache) ctx.rfiCache[contract.id] = undefined; window.dispatchEvent(new Event('outline-render')) })
  }, [contract.id]) // eslint-disable-line react-hooks/exhaustive-deps

  function toggleCollapse(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; window.dispatchEvent(new Event('outline-render')) }
  function addRfi(e) {
    e.stopPropagation()
    window.dispatchEvent(new CustomEvent('open-add-rfi-modal', { detail: { contractId: contract.id, projectId: contract.project_id } }))
  }
  function setAll(expand) {
    wf.collapsed[key] = !expand
    if (Array.isArray(rfis)) rfis.forEach(r => { wf.collapsed['rfi:' + r.id] = !expand })
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }
  function zoomIntoSection(e) { e.stopPropagation(); ctx.zoomToSection && ctx.zoomToSection(key, { root: 'construction' }) }
  const count = Array.isArray(rfis) ? rfis.length : null
  const unseenCount = Array.isArray(rfis) ? rfis.filter(isUnseen).length : 0

  const childrenContent = (
    <>
      {(rfis === null || rfis === undefined) && loadingRow('Loading…')}
      {Array.isArray(rfis) && rfis.length === 0 && loadingRow('No RFIs yet.')}
      {Array.isArray(rfis) && rfis.map(r => <RfiNode key={r.id} rfi={r} contract={contract} onChanged={onChanged} />)}
    </>
  )
  if (zoom) return childrenContent   // section-zoom: children only, no header row

  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Zoom into RFIs" data-kbzoom="1" onClick={zoomIntoSection}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">RFIs</span>
          {count != null && <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{count}</span>}
          {unseenCount > 0 && <NewPill n={unseenCount} />}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={addRfi}>Add RFI</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => setAll(true)}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={() => setAll(false)}>Collapse all</ActionMenuItem>
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          {childrenContent}
        </div>
      )}
    </div>
  )
}

/* ── Submittals (second CA sub-module, under a contract) ────────────────── */

function submittalStatusColor(s) { return s === 'closed' ? 'var(--aj-text-muted)' : '#1D9E75' }

function SubmittalDetailRows({ submittal: s }) {
  const rows = []
  if (s.gc_number)            rows.push(leafRow('GC submittal #', s.gc_number, { key: 'gc' }))
  if (s.type)                 rows.push(leafRow('Type', s.type, { key: 'type' }))
  if (Array.isArray(s.sections) && s.sections.length > 1) {
    rows.push(leafRow('Spec sections', s.sections.map(sec => [sec.section_number, sec.section_name].filter(Boolean).join(' — ')).filter(Boolean).join(', '), { key: 'spec' }))
  } else if (s.spec_section_number || s.spec_section_name) {
    rows.push(leafRow('Spec section', [s.spec_section_number, s.spec_section_name].filter(Boolean).join(' — '), { key: 'spec' }))
  }
  if (Array.isArray(s.reviewing_party) && s.reviewing_party.length) rows.push(leafRow('Reviewing party', s.reviewing_party.join(', '), { key: 'rev' }))
  if (Array.isArray(s.disposition) && s.disposition.length) rows.push(leafRow('Disposition', s.disposition.join(', '), { key: 'disp', color: '#1a6b4f' }))
  if (s.review_comments)      rows.push(leafTextRow('Review comments', s.review_comments, { key: 'rc', markdown: true }))
  if (s.date_received)        rows.push(leafRow('Received from GC', fmtDate(s.date_received), { key: 'dr' }))
  if (s.date_returned)        rows.push(leafRow('Returned to GC', fmtDate(s.date_returned), { key: 'dret' }))
  const tat = submittalTurnaround(s)
  if (tat != null) rows.push(leafRow('Turnaround', tat + ' day' + (tat !== 1 ? 's' : ''), { key: 'tat', color: 'var(--aj-text-muted)' }))
  if (!rows.length) rows.push(leafRow('Submittal', '—', { key: 'empty', color: 'var(--aj-text-muted)' }))
  return <>{rows}</>
}

/* Attachment rows (open in a new tab). `files`: undefined/null = loading, [] = none. */
function FileRowList({ files, onDelete }) {
  if (files === undefined || files === null) return loadingRow('Loading files…')
  if (!files.length) return loadingRow('No attachments — use ⋯ Upload.')
  return files.map(f => (
    <div key={f.id} className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" />
        <div className="wf-text-wrap">
          <span style={{ fontSize: 12 }}>📎</span>
          <a className="wf-node-text" href={f.url} target="_blank" rel="noopener noreferrer"
            style={{ marginLeft: 6, color: '#2563eb', textDecoration: 'none' }}
            onClick={e => e.stopPropagation()}>{f.filename}</a>
          <span className="wf-meta" style={{ fontSize: 10, color: '#9bb2b7' }}>
            {f.file_type ? f.file_type.toUpperCase() : ''}{f.file_size ? ' · ' + (f.file_size / 1024 / 1024).toFixed(1) + ' MB' : ''}
          </span>
          {onDelete && <button onClick={e => { e.stopPropagation(); onDelete(f.id) }} style={{ marginLeft: 8, fontSize: 10, color: '#c0392b', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Remove</button>}
        </div>
      </div>
    </div>
  ))
}

/* The review for one submission round. Draft/posted, disposition, transmittal icon, review markup. */
function ReviewNode({ submittal: s, rev, versionNo, projectName, projectNumber }) {
  const key = 'review:' + (rev ? rev.id : s.id + ':v' + versionNo)
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  const fileRef = useRef(null)
  const loaded = useRef(false)
  const [files, setFiles] = useState(undefined)
  // A review can be DRAFT (saved in progress, possibly without a disposition yet) or POSTED. Draft
  // status must derive from is_draft + any review content — NOT from the disposition alone, or a draft
  // saved without a disposition wrongly reads "not started".
  const dispList = (rev && Array.isArray(rev.disposition)) ? rev.disposition : []
  const hasContent = !!(rev && (dispList.length > 0 || rev.review_comments || rev.date_sent || rev.date_received_back || rev.date_returned))
  const isDraft = !!(rev && rev.is_draft === true) && hasContent
  const isPosted = !!(rev && rev.is_draft !== true && dispList.length > 0)
  const reviewExists = isDraft || isPosted

  useEffect(() => {
    if (collapsed || !rev || loaded.current) return
    loaded.current = true
    getRevisionFiles(rev.id).then(fs => setFiles((fs || []).filter(f => f.kind === 'review'))).catch(() => setFiles([]))
  }, [collapsed, rev && rev.id]) // eslint-disable-line react-hooks/exhaustive-deps

  function toggle(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; window.dispatchEvent(new Event('outline-render')) }
  function openReview() {
    window.dispatchEvent(new CustomEvent('open-log-submittal-review', { detail: {
      submittal: s, revisionId: rev ? rev.id : null, revisionNumber: rev ? rev.revision_number : (versionNo - 1),
      dateReceived: (rev && rev.date_received) || s.date_received || '', projectName, projectNumber,
      review: rev ? {
        disposition: rev.disposition, review_comments: rev.review_comments,
        date_sent: rev.date_sent, date_received_back: rev.date_received_back, date_returned: rev.date_returned,
      } : null,
    } }))
  }
  async function handleUpload(e) {
    if (!rev) return
    const picked = Array.from(e.target.files || []); if (fileRef.current) fileRef.current.value = ''
    for (const file of picked) { try { const f = await uploadRevisionFile(rev.id, s.id, file, 'review'); setFiles(prev => Array.isArray(prev) ? [...prev, f] : [f]) } catch (_) { /* skip */ } }
    if (wf.collapsed[key] !== false) wf.collapsed[key] = false
    window.dispatchEvent(new Event('outline-render'))
  }
  async function handleDelete(id) { if (!(await confirmDialog({ title: 'Remove file?', message: 'Remove this file?', confirmLabel: 'Remove', danger: true }))) return; await deleteRevisionFile(id).catch(() => {}); setFiles(prev => Array.isArray(prev) ? prev.filter(f => f.id !== id) : prev) }
  async function handlePreviewTransmittal() {
    const sigs = ctx.currentMember?.fields?.['Signature']
    const signatureUrl = Array.isArray(sigs) && sigs.length ? sigs[0].url : null
    const signerName = ctx.currentMember?.fields?.['Member Name'] || ''
    const noSig = !signatureUrl

    const tab = window.open('', '_blank')
    if (tab) {
      const warn = noSig && signerName
        ? `<p style="margin-top:12px;font-size:12px;color:#7a5c00;background:#fffbeb;border:1px solid #c79a3a;border-radius:4px;padding:6px 10px">No signature on file — a cursive text signature will be used.</p>`
        : ''
      tab.document.write(`<body style="margin:0;font-family:'DM Sans',sans-serif;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;background:#f4f7f8;color:#07272D;font-size:14px;">Generating transmittal…${warn}</body>`)
      tab.document.close()
    }
    try {
      let url = rev && rev.transmittal_pdf_url
      if (!url) {
        const result = await generateSubmittalTransmittal({
          submittalId: s.id, revisionId: rev.id, revisionNumber: rev.revision_number ?? 0,
          projectName, projectNumber, submittalNumber: s.submittal_number || '', gcNumber: s.gc_number || '',
          type: s.type || '', specSectionNumber: s.spec_section_number || '', specSectionName: s.spec_section_name || '',
          reviewer: s.reviewing_party || '', disposition: dispList,
          dateReceived: rev.date_received || null, dateSent: rev.date_sent || null,
          dateReceivedBack: rev.date_received_back || null, dateReturned: rev.date_returned || null,
          reviewComments: rev.review_comments || '',
          signerName, signatureUrl,
        })
        url = result.url
      }
      if (tab) tab.location = url
      else window.open(url, '_blank')
    } catch (err) {
      if (tab) tab.close()
      await alertDialog({ title: 'Transmittal failed', message: (err && err.message) || 'Could not generate the transmittal PDF.' })
    }
  }

  return (
    <div className={`wf-node wf-node--item${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}><span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span></button>
        <span className="wf-bullet wf-bullet--item" />
        <div className="wf-text-wrap">
          <span className="wf-node-text">Review (v{versionNo})</span>
          <span className="wf-meta">
            {isPosted && <span style={{ fontSize: 11, color: '#1D9E75' }}>{dispList.join(', ')}</span>}
            {isDraft && <span style={{ fontSize: 11, color: '#c79a3a' }}>{dispList.length ? dispList.join(', ') : 'In progress'}</span>}
            {isDraft && <span style={{ fontSize: 10, color: '#c79a3a', fontFamily: '"DM Sans",sans-serif', fontWeight: 600 }}>DRAFT</span>}
            {!reviewExists && <span style={{ fontSize: 10, color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>not started</span>}
            {rev && rev.transmittal_pdf_url && (
              <a href={rev.transmittal_pdf_url} target="_blank" rel="noopener noreferrer" title="Open transmittal PDF"
                style={{ fontSize: 12, color: '#2563eb', textDecoration: 'none' }} onClick={e => e.stopPropagation()}>📄</a>
            )}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        {!reviewExists && <ActionMenuItem onClick={openReview}>Log review</ActionMenuItem>}
        {reviewExists && <ActionMenuItem onClick={openReview}>{isDraft ? 'Edit / post review' : 'Edit review'}</ActionMenuItem>}
        {rev && <ActionMenuItem onClick={handlePreviewTransmittal}>Preview transmittal</ActionMenuItem>}
        {rev && <ActionMenuItem onClick={() => fileRef.current?.click()}>Upload markup</ActionMenuItem>}
      </ActionMenu>
      <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={handleUpload} />
      {!collapsed && rev && (<div className="wf-children"><FileRowList files={files} onDelete={handleDelete} /></div>)}
    </div>
  )
}

/* One submission round: Initial Submission (v1) / Resubmittal (vN) → package attachments + Review. */
function SubmissionNode({ submittal: s, rev, versionNo, isFirst, isLatest, projectName, projectNumber }) {
  const key = 'submission:' + (rev ? rev.id : s.id + ':v1')
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  const fileRef = useRef(null)
  const loaded = useRef(false)
  const [files, setFiles] = useState(undefined)
  const dateRecv = (rev && rev.date_received) || s.date_received || null
  const reviewPosted = !!(rev && !rev.is_draft && Array.isArray(rev.disposition) && rev.disposition.length > 0)
  const needsResubmit = reviewPosted && rev.disposition.some(d => RESUBMIT_DISPOSITIONS.has(d))

  useEffect(() => {
    if (collapsed || loaded.current) return
    loaded.current = true
    const loader = isFirst ? getSubmittalFiles(s.id) : (rev ? getRevisionFiles(rev.id) : Promise.resolve([]))
    loader.then(fs => setFiles(isFirst ? (fs || []) : (fs || []).filter(f => f.kind !== 'review'))).catch(() => setFiles([]))
  }, [collapsed, rev && rev.id]) // eslint-disable-line react-hooks/exhaustive-deps

  function toggle(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; window.dispatchEvent(new Event('outline-render')) }
  async function handleUpload(e) {
    const picked = Array.from(e.target.files || []); if (fileRef.current) fileRef.current.value = ''
    for (const file of picked) {
      try {
        const f = isFirst ? await uploadSubmittalFile(s.id, file) : await uploadRevisionFile(rev.id, s.id, file, 'package')
        setFiles(prev => Array.isArray(prev) ? [...prev, f] : [f])
      } catch (_) { /* skip */ }
    }
    if (wf.collapsed[key] !== false) wf.collapsed[key] = false
    window.dispatchEvent(new Event('outline-render'))
  }
  async function handleDelete(id) { if (!(await confirmDialog({ title: 'Remove file?', message: 'Remove this file?', confirmLabel: 'Remove', danger: true }))) return; await deleteRevisionFile(id).catch(() => {}); setFiles(prev => Array.isArray(prev) ? prev.filter(f => f.id !== id) : prev) }
  function logResubmittal() { window.dispatchEvent(new CustomEvent('open-log-resubmittal', { detail: { submittal: s, parentRevision: rev } })) }
  function openReview() { window.dispatchEvent(new CustomEvent('open-submittal-review', { detail: { submittalId: s.id, revisionId: rev ? rev.id : null } })) }

  return (
    <div className={`wf-node wf-node--item${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}><span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span></button>
        <span className="wf-bullet wf-bullet--item" />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{isFirst ? 'Initial Submission' : 'Resubmittal'} (v{versionNo})</span>
          {dateRecv && <span className="wf-meta" style={{ fontFamily: '"DM Mono",monospace', fontSize: 10 }}>received {fmtDate(dateRecv)}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={openReview}>Review submittal</ActionMenuItem>
        <ActionMenuItem onClick={() => fileRef.current?.click()}>Upload package files</ActionMenuItem>
        {needsResubmit && isLatest && <ActionMenuItem onClick={logResubmittal}>Log resubmittal</ActionMenuItem>}
      </ActionMenu>
      <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={handleUpload} />
      {!collapsed && (
        <div className="wf-children">
          <FileRowList files={files} onDelete={handleDelete} />
          <ReviewNode submittal={s} rev={rev} versionNo={versionNo} projectName={projectName} projectNumber={projectNumber} />
        </div>
      )}
    </div>
  )
}

/* hideThreadMount: see RfiNode — breaks the thread↔CA mutual-mount recursion. */
export function SubmittalNode({ submittal: s, contract, onChanged, hideThreadMount, hideMilestoneMount, labelPrefix = '', mountCtx = '' }) {
  // mountCtx disambiguates this submittal's collapse state across its possible mounts
  // (Reference list, MilestoneSubmittalMount, ThreadCaMount) — see the identical AsiNode fix
  // note above.
  const key = 'submittal:' + s.id + (mountCtx ? ':' + mountCtx : '')
  const isCollapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  // Revision chain lives in a ctx cache so it re-loads after Log Review / Log Resubmittal bust it
  // (local state with a loaded-ref never refreshed in-session — the row isn't remounted).
  const revisions = ctx.submittalRevCache?.[s.id]
  useCaCacheReload('submittalRevCache', s.id, isCollapsed, getSubmittalRevisions)
  const sc = submittalStatusColor(s.status)
  const tat = submittalTurnaround(s)
  const label = submittalLabel(s)
  // Badge: PDF icon if the current revision has a transmittal PDF
  const hasPdf = !!(s.transmittal_pdf_url || (Array.isArray(revisions) && revisions.some(r => r.transmittal_pdf_url)))
  // Resubmittal indicator: latest review disposition requires the GC to send something back
  const awaitingResubmittal = Array.isArray(s.disposition) && s.disposition.some(d => RESUBMIT_DISPOSITIONS.has(d))
  // Needs-review indicator: a package has been received but not yet reviewed (resubmittal logged).
  const noDisp = !Array.isArray(s.disposition) || s.disposition.length === 0
  const awaitingReview = s.status !== 'closed' && noDisp && !!s.date_received && !s.date_returned
  // Contextual action label: "Log Resubmittal" when a resubmittal is expected
  const reviewActionLabel = awaitingResubmittal ? 'Log Resubmittal' : 'Log Review'
  const unseen = isUnseen(s)
  // Clear the NEW highlight the first time a team member opens (expands or zooms into) this submittal.
  function markSeen() {
    if (!isUnseen(s)) return
    s.team_seen_at = new Date().toISOString()
    markSubmittalSeen(s.id).catch(() => {})
    window.dispatchEvent(new Event('outline-render'))
  }

  // Load revision chain when expanded
  useEffect(() => {
    if (isCollapsed) return
    if (!ctx.submittalRevCache) ctx.submittalRevCache = {}
    if (ctx.submittalRevCache[s.id] !== undefined) return // null = in-flight, [...] = loaded
    ctx.submittalRevCache[s.id] = null
    getSubmittalRevisions(s.id)
      .then(r => { ctx.submittalRevCache[s.id] = r; window.dispatchEvent(new Event('outline-render')) })
      .catch(() => { ctx.submittalRevCache[s.id] = undefined; window.dispatchEvent(new Event('outline-render')) })
  }, [isCollapsed, s.id]) // eslint-disable-line react-hooks/exhaustive-deps

  function toggleCollapse(e) { e.stopPropagation(); const opening = isCollapsed; wf.collapsed[key] = !isCollapsed; if (opening) markSeen(); window.dispatchEvent(new Event('outline-render')) }
  function zoomIn(e) { e.stopPropagation(); markSeen(); ctx.zoomToSection && ctx.zoomToSection('submittal-detail:' + s.id, { root: 'construction' }) }
  function edit() {
    window.dispatchEvent(new CustomEvent('open-add-submittal-modal', { detail: {
      contractId: contract.id, projectId: s.project_id || contract.project_id, submittalId: s.id,
      prefill: { gc_number: s.gc_number, type: s.type, spec_section_number: s.spec_section_number, spec_section_name: s.spec_section_name, spec_section_id: s.spec_section_id, sections: s.sections, submittal_number: s.submittal_number, reviewing_party: s.reviewing_party, disposition: s.disposition, review_comments: s.review_comments, date_received: s.date_received, date_returned: s.date_returned, status: s.status, thread_airtable_id: s.thread_airtable_id, milestone_airtable_id: s.milestone_airtable_id },
    } }))
  }
  function logReview() {
    // Resolve project name from ctx state
    const ms = (ctx.state?.milestones || [])[0]
    const pName = (ms?.fields?.['Project Name'] || [])[0] || ms?.fields?.['Project Name'] || ''
    const pNum = (ms?.fields?.['Project Number'] || [])[0] || ms?.fields?.['Project Number'] || ''
    window.dispatchEvent(new CustomEvent('open-log-submittal-review', { detail: {
      submittal: s,
      projectName: pName,
      projectNumber: pNum,
    } }))
  }
  async function del() {
    if (!(await confirmDialog({ title: 'Delete submittal?', message: 'Delete this submittal?', confirmLabel: 'Delete submittal', danger: true }))) return
    // If the submittal has a linked thread, offer to delete it too (full cleanup: thread +
    // activity log). deleteThreadCascade removes the linked submittal (and its revisions/files via the
    // delete cascade) as well, so we don't also call deleteSubmittal.
    let alsoThread = false
    if (s.thread_airtable_id) {
      alsoThread = await confirmDialog({
        title: 'Delete the linked thread too?',
        message: 'This submittal has a linked Thread. Delete it as well — including its full activity log? This can’t be undone.',
        confirmLabel: 'Delete thread too', cancelLabel: 'Keep thread', danger: true,
      })
    }
    const projectId = s.project_id || contract.project_id
    const op = alsoThread ? deleteThreadCascade(s.thread_airtable_id) : deleteSubmittal(s.id)
    op.then(async () => {
      if (ctx.submittalCache) ctx.submittalCache[contract.id] = undefined
      if (ctx.milestoneSubmittalCache && s.milestone_airtable_id) ctx.milestoneSubmittalCache[s.milestone_airtable_id] = undefined
      if (ctx.threadCaItemCache && s.thread_airtable_id) ctx.threadCaItemCache[s.thread_airtable_id] = undefined
      if (alsoThread) {
        if (ctx.caThreadCache) delete ctx.caThreadCache[s.thread_airtable_id]
        if (projectId) { try { ctx.threadCache = await getProjectThreads(projectId) } catch (_) { /* non-fatal */ } }
      }
      onChanged && onChanged()
      window.dispatchEvent(new Event('outline-render'))
    }).catch(e => alertDialog({ title: 'Delete failed', message: e.message || String(e) }))
  }

  return (
    <div className={`wf-node wf-node--item${isCollapsed ? ' wf-node--collapsed' : ''}${s.status === 'closed' ? ' wf-node--is-done' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--item" title="Zoom into submittal" data-kbzoom="1" onClick={zoomIn}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={unseen ? { fontWeight: 700 } : undefined}>{labelPrefix}{label}{unseen && <NewPill />}</span>
          <span className="wf-meta">
            {s.submittal_number && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)' }}>{s.submittal_number}</span>}
            {s.type && <span style={{ fontSize: 10, color: 'var(--aj-text-muted)' }}>{s.type}</span>}
            <span className="wf-status-pill" style={{ color: sc, borderColor: sc + '33' }}>{SUBMITTAL_STATUS_LABELS[s.status] || s.status}</span>
            {tat != null && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)' }}>{tat}d</span>}
            {hasPdf && <span title="Transmittal PDF available" style={{ fontSize: 11, color: '#2563eb' }}>📄</span>}
            {awaitingResubmittal && <span title="Awaiting resubmittal from GC" style={{ fontSize: 10, color: '#c79a3a', fontFamily: '"DM Sans",sans-serif' }}>↩ Awaiting resubmittal</span>}
            {awaitingReview && <span title="Resubmittal received — needs review" style={{ fontSize: 10, color: '#2563eb', fontFamily: '"DM Sans",sans-serif' }}>🔎 Needs review</span>}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={edit}>Edit submittal</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={del} danger>Delete submittal</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          {/* Milestone (or, for legacy items, the thread it owns status/deadline/activity/Slack from) */}
          {!hideMilestoneMount && (s.milestone_airtable_id
            ? <CaMilestoneMount milestoneId={s.milestone_airtable_id} projectId={s.project_id} />
            : (!hideThreadMount && <CaThreadMount threadId={s.thread_airtable_id} projectId={s.project_id} />))}

          {/* Submission rounds: Initial Submission (v1) → Resubmittal (v2)…, each with package
              attachments + its Review (draft/posted, disposition, transmittal). */}
          {(revisions === undefined || revisions === null) && loadingRow('Loading…')}
          {Array.isArray(revisions) && (() => {
            const ms0 = (ctx.state?.milestones || [])[0]
            const pName = (ms0?.fields?.['Project Name'] || [])[0] || ms0?.fields?.['Project Name'] || ''
            const pNum  = (ms0?.fields?.['Project Number'] || [])[0] || ms0?.fields?.['Project Number'] || ''
            const rev0 = revisions.find(r => r.revision_number === 0) || null
            const resubs = revisions.filter(r => (r.revision_number || 0) >= 1).sort((a, b) => a.revision_number - b.revision_number)
            const latestNo = resubs.length ? resubs[resubs.length - 1].revision_number : 0
            return (
              <>
                <SubmissionNode submittal={s} rev={rev0} versionNo={1} isFirst isLatest={latestNo === 0} projectName={pName} projectNumber={pNum} />
                {resubs.map(r => (
                  <SubmissionNode key={r.id} submittal={s} rev={r} versionNo={r.revision_number + 1} isLatest={r.revision_number === latestNo} projectName={pName} projectNumber={pNum} />
                ))}
              </>
            )
          })()}
        </div>
      )}
    </div>
  )
}

/* Submittals sub-branch under a contract — lazy-loads ctx.submittalCache[contractId]. */
function SubmittalList({ contract, onChanged, zoom }) {
  const key = 'sec:contract:' + contract.id + ':submittals'
  const collapsed = wf.collapsed[key] !== false
  const open = zoom || !collapsed   // zoom always shows children
  const subs = ctx.submittalCache?.[contract.id]
  const menu = useActionMenu()
  useCaCacheReload('submittalCache', contract.id, false, getContractSubmittals)

  // Eager-load (not gated by collapse) so the NEW badge + count surface on the collapsed node —
  // a GC's submittal now posts directly here, so the firm needs to see it without expanding first.
  useEffect(() => {
    if (subs !== undefined) return
    if (!ctx.submittalCache) ctx.submittalCache = {}
    ctx.submittalCache[contract.id] = null
    getContractSubmittals(contract.id).then(r => {
      if (!ctx.submittalCache) ctx.submittalCache = {}
      ctx.submittalCache[contract.id] = r
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => { if (ctx.submittalCache) ctx.submittalCache[contract.id] = undefined; window.dispatchEvent(new Event('outline-render')) })
  }, [contract.id]) // eslint-disable-line react-hooks/exhaustive-deps

  function toggleCollapse(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; window.dispatchEvent(new Event('outline-render')) }
  function addSubmittal(e) {
    e.stopPropagation()
    window.dispatchEvent(new CustomEvent('open-add-submittal-modal', { detail: { contractId: contract.id, projectId: contract.project_id } }))
  }
  function setAll(expand) {
    wf.collapsed[key] = !expand
    if (Array.isArray(subs)) subs.forEach(s => { wf.collapsed['submittal:' + s.id] = !expand })
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }
  function zoomIntoSection(e) { e.stopPropagation(); ctx.zoomToSection && ctx.zoomToSection(key, { root: 'construction' }) }
  const count = Array.isArray(subs) ? subs.length : null
  const unseenCount = Array.isArray(subs) ? subs.filter(isUnseen).length : 0

  const childrenContent = (
    <>
      {(subs === null || subs === undefined) && loadingRow('Loading…')}
      {Array.isArray(subs) && subs.length === 0 && loadingRow('No submittals yet.')}
      {Array.isArray(subs) && subs.map(s => <SubmittalNode key={s.id} submittal={s} contract={contract} onChanged={onChanged} />)}
    </>
  )
  if (zoom) return childrenContent   // section-zoom: children only, no header row

  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Zoom into Submittals" data-kbzoom="1" onClick={zoomIntoSection}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Submittals</span>
          {count != null && <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{count}</span>}
          {unseenCount > 0 && <NewPill n={unseenCount} />}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={addSubmittal}>Add submittal</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => setAll(true)}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={() => setAll(false)}>Collapse all</ActionMenuItem>
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          {childrenContent}
        </div>
      )}
    </div>
  )
}

/* ── ASIs (Architect's Supplemental Instructions, under a contract) ──────── */

function asiStatusColor(s) { return s === 'issued' ? '#1D9E75' : s === 'rescinded' ? '#c0392b' : 'var(--aj-text-muted)' }

/* A leaf row that is click-to-edit when `editable` — the ASI's fields no longer go through a
   single big modal (Round 22); each field owns its own edit action via promptDialog, matching
   the SubtaskRow idiom. Short fields render inline in the meta slot; `markdown` fields render
   full-width below the label using the same wrapping treatment as leafTextRow. Locked (not
   `editable`, i.e. status === 'issued') fields with no value are hidden entirely — nothing to
   show and nothing to add. */
function AsiEditableRow({ label, value, editable, markdown, onEdit, keyName }) {
  const hasValue = value != null && value !== ''
  if (!hasValue && !editable) return null
  const empty = <span style={{ fontStyle: 'italic' }}>Add {label.toLowerCase()}…</span>
  return (
    <div className="wf-node wf-node--item" key={keyName || label}>
      <div className="wf-row" style={markdown ? { alignItems: 'flex-start' } : undefined}>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={markdown ? { marginTop: 6 } : undefined}></span>
        <div className="wf-text-wrap" style={markdown ? { flexDirection: 'column', alignItems: 'stretch', minWidth: 0 } : undefined}>
          <span className="wf-node-text">{label}</span>
          {markdown ? (
            <div
              className="wf-node-text"
              onClick={editable ? onEdit : undefined}
              style={{
                marginTop: 2, whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word',
                cursor: editable ? 'pointer' : 'default',
                color: hasValue ? undefined : 'var(--aj-text-mid)',
              }}
            >
              {hasValue ? <Markdown text={value} /> : empty}
            </div>
          ) : (
            <span className="wf-meta">
              <span
                className="wf-node-text"
                onClick={editable ? onEdit : undefined}
                style={{ color: hasValue ? undefined : 'var(--aj-text-mid)', cursor: editable ? 'pointer' : 'default' }}
              >
                {hasValue ? value : empty}
              </span>
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

/* Bound attachments — supplemental PDF(s) (each may already be multi-page) that get physically
   merged onto the end of the generated ASI cover sheet at issue time (api/_asiPdf.js). The issued
   PDF itself (document_url) is never user-uploaded — it's system-generated only, at issue/reissue
   time. Each attachment upload stages to the asi-docs bucket via getAsiUploadUrl, then the
   resulting {filename,url,path} is appended to the asi.attachments jsonb array via onSave. */
function AsiAttachmentsRow({ asi: a, editable, onSave }) {
  const [uploading, setUploading] = useState(false)
  const items = Array.isArray(a.attachments) ? a.attachments : []
  if (!items.length && !editable) return null

  async function handleFiles(e) {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (!files.length) return
    setUploading(true)
    try {
      const uploaded = []
      for (const file of files) {
        const { uploadUrl, publicUrl, path } = await getAsiUploadUrl(a.id, file.name)
        const res = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/pdf' } })
        if (!res.ok) throw new Error('Upload failed (' + res.status + ')')
        uploaded.push({ filename: file.name, url: publicUrl, path })
      }
      await onSave({ attachments: [...items, ...uploaded] })
    } catch (err) {
      alertDialog({ title: 'Upload failed', message: err.message || String(err) })
    } finally {
      setUploading(false)
    }
  }

  async function remove(idx) {
    if (!(await confirmDialog({ title: 'Remove attachment?', message: 'Remove this attachment? It will no longer be bound into the ASI PDF.', confirmLabel: 'Remove', danger: true }))) return
    onSave({ attachments: items.filter((_, i) => i !== idx) })
  }

  return (
    <div className="wf-node wf-node--item" key="attfiles">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item"></span>
        <div className="wf-text-wrap" style={{ flexDirection: 'column', alignItems: 'stretch', minWidth: 0 }}>
          <span className="wf-node-text">Bound Attachments (PDF)</span>
          {items.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
              {items.map((it, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: '"DM Sans",sans-serif', fontSize: 11 }}>
                  <a href={it.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: '#2563eb', textDecoration: 'none' }}>{it.filename}</a>
                  {editable && (
                    <span onClick={() => remove(i)} style={{ color: 'var(--aj-text-mid)', cursor: 'pointer', textDecoration: 'underline' }}>Remove</span>
                  )}
                </div>
              ))}
            </div>
          )}
          {editable && (
            <label style={{ marginTop: items.length ? 6 : 2, fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: uploading ? 'var(--aj-text-mid)' : 'var(--aj-text)', cursor: uploading ? 'default' : 'pointer', textDecoration: 'underline', alignSelf: 'flex-start' }}>
              {uploading ? 'Uploading…' : '+ Add PDF attachment(s)'}
              <input type="file" accept="application/pdf" multiple onChange={handleFiles} disabled={uploading} style={{ display: 'none' }} />
            </label>
          )}
        </div>
      </div>
    </div>
  )
}

/* All editable content fields for an ASI, plus the read-only rescind/sheets rows. `editable`
   mirrors the server-side lock in api/asis.js (status !== 'issued'). `onSave(fields)` PATCHes
   and patches the cache — provided by AsiNode. */
function AsiDetailRows({ asi: a, editable, onSave }) {
  async function editShort(label, key, opts = {}) {
    const current = a[key]
    const defaultValue = current == null ? '' : String(current)
    const val = await promptDialog({ title: 'Edit ' + label, defaultValue, placeholder: opts.placeholder, dialogId: 'asi-field-' + key })
    if (val === null) return
    const trimmed = val.trim()
    if (trimmed === defaultValue) return
    let next = trimmed === '' ? null : trimmed
    if (opts.numeric && next != null) {
      const n = Number(next)
      if (Number.isNaN(n)) { alertDialog({ title: 'Invalid number', message: label + ' must be a number.' }); return }
      next = n
    }
    onSave({ [key]: next })
  }

  async function editDate() {
    const val = await promptDialog({ title: 'Edit issue date', defaultValue: a.issue_date || '', inputType: 'date', dialogId: 'asi-field-issue_date' })
    if (val === null) return
    const trimmed = val.trim()
    if (trimmed === (a.issue_date || '')) return
    onSave({ issue_date: trimmed || null })
  }

  // Sender is always an internal team member (the architect issuing the ASI) — picked from the
  // Members roster rather than typed, so it's a real reference (sender_member_id), not a
  // free-text guess at spelling. sender_name is written alongside it as the denormalized display
  // value the issued PDF prints (never re-derived live).
  async function editSender() {
    const members = await getTeamMembers().catch(() => [])
    const NONE = '__none__'   // choiceDialog treats '' as "nothing selected" (Save stays disabled), so a real sentinel is needed for the clear option
    const choices = [{ value: NONE, label: '— None —' }, ...members.map(m => ({ value: m.id, label: m.name }))]
    const r = await choiceDialog({ title: 'Sender', message: 'Who is issuing this ASI?', choices, dialogId: 'asi-field-sender' })
    if (!r) return
    const nextId = r.value === NONE ? '' : r.value
    if (nextId === (a.sender_member_id || '')) return
    const member = members.find(m => m.id === nextId)
    onSave({ sender_member_id: nextId || null, sender_name: member?.name || null })
  }

  async function editMarkdown(label, key) {
    const current = a[key] || ''
    const val = await promptDialog({ title: 'Edit ' + label, defaultValue: current, multiline: true, dialogId: 'asi-field-' + key })
    if (val === null) return
    const trimmed = val.trim()
    if (trimmed === current) return
    onSave({ [key]: trimmed || null })
  }

  return (
    <>
      <AsiEditableRow keyName="num" label="ASI #" value={a.asi_number} editable={editable} onEdit={() => editShort('ASI #', 'asi_number', { numeric: true })} />
      <AsiEditableRow keyName="issue" label="Issue date" value={a.issue_date ? fmtDate(a.issue_date) : null} editable={editable} onEdit={editDate} />
      <AsiEditableRow keyName="subj" label="Subject" value={a.subject} editable={editable} onEdit={() => editShort('Subject', 'subject')} />
      <AsiEditableRow keyName="sender" label="Sender" value={a.sender_name} editable={editable} onEdit={editSender} />
      <AsiEditableRow keyName="desc" label="Detailed description" value={a.detailed_description} editable={editable} markdown onEdit={() => editMarkdown('Detailed Description', 'detailed_description')} />
      <AsiEditableRow keyName="att" label="Attachments" value={a.attachment_list} editable={editable} markdown onEdit={() => editMarkdown('Attachment List', 'attachment_list')} />
      <AsiAttachmentsRow asi={a} editable={editable} onSave={onSave} />
      {a.status === 'rescinded' && leafRow('Rescinded', a.rescinded_at ? fmtDate(a.rescinded_at) : '—', { key: 'resc', color: '#c0392b' })}
      {a.status === 'rescinded' && a.rescind_note && leafTextRow('Rescind note', a.rescind_note, { key: 'rescnote' })}
      {Array.isArray(a.sheets_included) && a.sheets_included.length > 0 && leafRow('Sheets included', a.sheets_included.length + ' sheet' + (a.sheets_included.length !== 1 ? 's' : ''), { key: 'sheets', color: 'var(--aj-text-muted)' })}
    </>
  )
}

export function AsiNode({ asi: a, contract, onChanged, hideMilestoneMount, labelPrefix = '', mountCtx = '' }) {
  // mountCtx disambiguates the SAME asi's collapse state across the two places it can render
  // (Reference > Construction's own list, and MilestoneAsiMount's reverse mount under its
  // milestone) — without it both copies shared one wf.collapsed key, so expanding/collapsing
  // one silently did the same to the other (Matt, 2026-09-17).
  const key = 'asi:' + a.id + (mountCtx ? ':' + mountCtx : '')
  const isCollapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  const sc = asiStatusColor(a.status)
  const wasEverIssued = a.status !== 'draft'

  const editable = a.status !== 'issued'   // matches the server-side lock in api/asis.js

  function toggleCollapse(e) { e.stopPropagation(); wf.collapsed[key] = !isCollapsed; window.dispatchEvent(new Event('outline-render')) }
  function zoomIn(e) { e.stopPropagation(); ctx.zoomToSection && ctx.zoomToSection('asi-detail:' + a.id, { root: 'construction' }) }
  function applyUpdated(updated) {
    if (ctx.asiCache && contract.id) {
      const cached = ctx.asiCache[contract.id]
      if (Array.isArray(cached)) {
        const idx = cached.findIndex(x => x.id === a.id)
        if (idx !== -1) cached[idx] = updated
      }
    }
    // This same ASI may ALSO be mounted (stale) under its milestone (MilestoneAsiMount) if this
    // edit came from the Reference-branch copy — bust that cache too so neither mount is left
    // showing what was there before the edit (Matt, 2026-09-17).
    if (ctx.milestoneAsiCache && updated.milestone_airtable_id) ctx.milestoneAsiCache[updated.milestone_airtable_id] = undefined
    onChanged && onChanged()
    window.dispatchEvent(new Event('outline-render'))
  }
  // Per-field save for the inline-editable detail rows (Round 22 — replaces the old single
  // "Edit ASI" modal). Each row calls this with just the field(s) it changed.
  async function saveField(fields) {
    try {
      applyUpdated(await updateAsi(a.id, fields))
    } catch (e) {
      alertDialog({ title: 'Save failed', message: e.message || String(e) })
    }
  }
  async function doIssue() {
    const isReissue = a.status === 'rescinded'
    const verb = isReissue ? 'Reissue' : 'Issue'
    const msg = `${verb} ASI${a.asi_number ? ' #' + a.asi_number : ''}? This will notify subscribed contacts and make it visible in the GC portal.`
    if (!(await confirmDialog({ title: `${verb} ASI?`, message: msg, confirmLabel: `${verb} ASI` }))) return
    try {
      applyUpdated(await issueAsi(a.id))
    } catch (e) { alertDialog({ title: `${verb} failed`, message: e.message || String(e) }) }
  }
  async function doRescind() {
    const note = await promptDialog({
      title: 'Rescind ASI' + (a.asi_number ? ' #' + a.asi_number : '') + '?',
      message: 'This hides its details from the GC portal (a rescinded placeholder stays visible for transparency) and notifies subscribed contacts. Optional note for the record and the email:',
      placeholder: 'e.g. issued in error, scope changed…',
      multiline: true,
    })
    if (note === null) return
    try {
      applyUpdated(await rescindAsi(a.id, note))
    } catch (e) { alertDialog({ title: 'Rescind failed', message: e.message || String(e) }) }
  }
  async function del() {
    if (!(await confirmDialog({ title: 'Delete ASI?', message: 'Delete this ASI?', confirmLabel: 'Delete ASI', danger: true }))) return
    deleteAsi(a.id).then(() => {
      if (ctx.asiCache) ctx.asiCache[contract.id] = undefined
      if (ctx.milestoneAsiCache && a.milestone_airtable_id) ctx.milestoneAsiCache[a.milestone_airtable_id] = undefined
      onChanged && onChanged(); window.dispatchEvent(new Event('outline-render'))
    })
      .catch(e => alertDialog({ title: 'Delete failed', message: e.message || String(e) }))
  }
  // Backfill/refresh the PDF with no status/date/notification change — for ASIs issued before the
  // native exporter existed (document_url is null), or to regenerate one after a template fix.
  async function doGeneratePdf() {
    try {
      applyUpdated(await generateAsiPdf(a.id))
    } catch (e) { alertDialog({ title: 'PDF generation failed', message: e.message || String(e) }) }
  }
  // Draft-stamped preview using the ASI's current field values + bound attachments — not saved,
  // so this is safe to run any number of times before Issue is clicked.
  async function doPreview() {
    const tab = window.open('', '_blank')
    try {
      const res = await previewAsiPdf(a.id)
      if (res.error) { if (tab) tab.close(); await alertDialog({ title: 'Preview failed', message: res.error }); return }
      if (tab) tab.location = res.pdfUrl
      else window.open(res.pdfUrl, '_blank')
    } catch (e) {
      if (tab) tab.close()
      await alertDialog({ title: 'Preview failed', message: e.message || String(e) })
    }
  }
  // Manual link repair — allowed at any status (linkage, not content). A handful of legacy ASIs
  // predate the auto-create-a-milestone flow and are still stuck on a stale thread link.
  function editMilestoneLink() {
    window.dispatchEvent(new CustomEvent('open-edit-asi-milestone-modal', { detail: {
      asiId: a.id, projectId: a.project_id || contract.project_id, contractId: contract.id,
      currentMilestoneId: a.milestone_airtable_id || null, asiNumber: a.asi_number, subject: a.subject,
    } }))
  }

  return (
    <div className={`wf-node wf-node--item${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--item" title="Zoom into ASI" data-kbzoom="1" onClick={zoomIn}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text">
            {labelPrefix}
            {a.asi_number != null && <span style={{ fontFamily: '"DM Mono",monospace', color: 'var(--aj-text-muted)', marginRight: 6 }}>#{a.asi_number}</span>}
            {a.subject || 'ASI'}
          </span>
          <span className="wf-meta" style={{ marginLeft: 'auto' }}>
            {a.issue_date && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)' }}>{fmtDate(a.issue_date)}</span>}
            <span className="wf-status-pill" style={{ color: sc, borderColor: sc + '33' }}>{ASI_STATUS_LABELS[a.status] || a.status}</span>
            {a.document_url && (
              <a href={a.document_url} target="_blank" rel="noopener noreferrer" title="Open ASI PDF"
                style={{ fontSize: 12, color: '#2563eb', textDecoration: 'none' }} onClick={e => e.stopPropagation()}>📄</a>
            )}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        {(a.status === 'draft' || a.status === 'rescinded') && <ActionMenuItem onClick={doPreview}>Preview PDF</ActionMenuItem>}
        {a.status === 'draft' && <ActionMenuItem onClick={doIssue}>Issue ASI</ActionMenuItem>}
        {a.status === 'rescinded' && <ActionMenuItem onClick={doIssue}>Reissue ASI</ActionMenuItem>}
        {a.status === 'issued' && <ActionMenuItem onClick={doRescind}>Rescind ASI</ActionMenuItem>}
        {a.status === 'issued' && <ActionMenuItem onClick={doGeneratePdf}>{a.document_url ? 'Regenerate PDF' : 'Generate PDF'}</ActionMenuItem>}
        <ActionMenuDivider />
        <ActionMenuItem onClick={editMilestoneLink}>Edit milestone link</ActionMenuItem>
        {!wasEverIssued && (
          <>
            <ActionMenuDivider />
            <ActionMenuItem onClick={del} danger>Delete ASI</ActionMenuItem>
          </>
        )}
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          <AsiDetailRows asi={a} editable={editable} onSave={saveField} />
          {!hideMilestoneMount && (a.milestone_airtable_id
            ? <CaMilestoneMount milestoneId={a.milestone_airtable_id} projectId={a.project_id} />
            : <CaThreadMount threadId={a.thread_airtable_id} projectId={a.project_id} />)}
        </div>
      )}
    </div>
  )
}

/* ASIs sub-branch under a contract — lazy-loads ctx.asiCache[contractId]. */
function AsiList({ contract, onChanged, zoom }) {
  const key = 'sec:contract:' + contract.id + ':asis'
  const collapsed = wf.collapsed[key] !== false
  const open = zoom || !collapsed   // zoom always shows children
  const asis = ctx.asiCache?.[contract.id]
  const menu = useActionMenu()
  useCaCacheReload('asiCache', contract.id, !open, getContractAsis)

  useEffect(() => {
    if (!open) return
    if (asis !== undefined) return
    if (!ctx.asiCache) ctx.asiCache = {}
    ctx.asiCache[contract.id] = null
    getContractAsis(contract.id).then(r => {
      if (!ctx.asiCache) ctx.asiCache = {}
      ctx.asiCache[contract.id] = r
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => { if (ctx.asiCache) ctx.asiCache[contract.id] = undefined; window.dispatchEvent(new Event('outline-render')) })
  }, [open, contract.id]) // eslint-disable-line react-hooks/exhaustive-deps

  function toggleCollapse(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; window.dispatchEvent(new Event('outline-render')) }
  function addAsi(e) {
    e.stopPropagation()
    window.dispatchEvent(new CustomEvent('open-add-asi-modal', { detail: { contractId: contract.id, projectId: contract.project_id } }))
  }
  function setAll(expand) {
    wf.collapsed[key] = !expand
    if (Array.isArray(asis)) asis.forEach(a => { wf.collapsed['asi:' + a.id] = !expand })
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }
  function zoomIntoSection(e) { e.stopPropagation(); ctx.zoomToSection && ctx.zoomToSection(key, { root: 'construction' }) }
  const count = Array.isArray(asis) ? asis.length : null

  const childrenContent = (
    <>
      {(asis === null || asis === undefined) && loadingRow('Loading…')}
      {Array.isArray(asis) && asis.length === 0 && loadingRow('No ASIs yet.')}
      {Array.isArray(asis) && asis.map(a => <AsiNode key={a.id} asi={a} contract={contract} onChanged={onChanged} />)}
    </>
  )
  if (zoom) return childrenContent   // section-zoom: children only, no header row

  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Zoom into ASIs" data-kbzoom="1" onClick={zoomIntoSection}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">ASIs</span>
          {count != null && <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{count}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={addAsi}>Add ASI</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => setAll(true)}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={() => setAll(false)}>Collapse all</ActionMenuItem>
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          {childrenContent}
        </div>
      )}
    </div>
  )
}

/* ── Spec Sections subsection ───────────────────────────────────────────── */
/* Status badge colors */
const SPEC_STATUS_COLOR = { draft: '#9bb2b7', issued: '#1D9E75', superseded: '#c0392b' }
const REGISTER_STATUS_COLOR = { pending: '#c79a3a', received: '#1D9E75', returned: '#2563eb', approved: '#1D9E75' }

// Badge style per spec revision type — mirrors the Drawing Sheet REV_TYPE_STYLE look.
const SPEC_REV_STYLE = {
  'Original Release': { bg: '#e8f5f0', color: '#1D9E75', label: 'Original' },
  'Carried Forward':  { bg: '#e8f1f8', color: '#1a6fa8', label: 'Carried Fwd' },
  'Revision':         { bg: '#fef3e8', color: '#c87941', label: 'Revision' },
}

// Open this version of the section as a standalone PDF (preview tab). Pre-opens the window before the
// async export so it isn't caught by the popup blocker — same trick the sheet/export paths use.
function openSpecVersionPdf(sectionId, versionId, projectId, projectName) {
  const w = (typeof window !== 'undefined') ? window.open('', '_blank') : null
  import('../services/specifications.js')
    .then(m => m.exportSpecPdf(projectId, null, projectName || '', { mode: 'section', sectionId, versionId, targetWindow: w }))
    .catch(e => { try { if (w && !w.closed) w.close() } catch { /* ignore */ } alertDialog({ title: 'PDF failed', message: e.message || String(e) }) })
}

// Revisions list — styled to match the Drawing Sheet revision rows (type badge · milestone title ·
// change narrative · date · LATEST · PDF). Each version opens as its own PDF.
function SpecSectionVersionList({ sectionId, section, projectId }) {
  const [versions, setVersions] = useState(undefined)
  const hasLoaded = useRef(false)
  const collapsed = wf.collapsed['spec-ver:' + sectionId] !== false
  useEffect(() => {
    if (collapsed || hasLoaded.current) return
    hasLoaded.current = true
    getSpecSectionVersions(sectionId).then(v => { setVersions(v); window.dispatchEvent(new Event('outline-render')) })
  }, [collapsed, sectionId])
  if (collapsed) return null
  const maxVer = Array.isArray(versions) ? versions.reduce((m, v) => Math.max(m, v.version_number || 0), 0) : 0
  const ordered = Array.isArray(versions) ? [...versions].sort((a, b) => (b.version_number || 0) - (a.version_number || 0)) : []
  const projectName = (section && section.project_name) || ctx.projectName || ''
  return (
    <div className="wf-children">
      {versions === undefined && loadingRow('Loading versions…')}
      {Array.isArray(versions) && versions.length === 0 && loadingRow('No versions yet. Add a version to issue this section.')}
      {ordered.map(v => {
        const st = SPEC_REV_STYLE[v.revision_type] || { bg: '#f4f7f8', color: 'var(--aj-text-muted)', label: v.revision_type || '—' }
        const isLatest = (v.version_number || 0) === maxVer
        return (
          <div key={v.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '5px 8px 5px 38px' }}>
            <span style={{ background: st.bg, color: st.color, fontSize: 9, fontFamily: '"DM Mono", monospace', fontWeight: 600, padding: '2px 5px', borderRadius: 3, flexShrink: 0, marginTop: 1, letterSpacing: '0.04em' }}>{st.label}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11.5, color: 'var(--aj-text)', fontFamily: '"DM Sans", sans-serif', fontWeight: 500, lineHeight: 1.3 }}>
                {v.milestone_name || ('Version ' + v.version_number)}
              </div>
              {v.changes_summary && (
                <div style={{ fontSize: 11.5, color: 'var(--aj-text-mid)', fontFamily: '"DM Sans", sans-serif', lineHeight: 1.4 }}>{v.changes_summary}</div>
              )}
            </div>
            {v.issued_date && (
              <span style={{ fontSize: 11, color: 'var(--aj-text-muted)', fontFamily: '"DM Mono", monospace', flexShrink: 0, marginTop: 1 }}>{fmtDate(v.issued_date)}</span>
            )}
            {isLatest && (
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.07em', color: '#1D9E75', flexShrink: 0, marginTop: 2, fontFamily: '"DM Mono", monospace' }}>LATEST</span>
            )}
            <button onClick={() => openSpecVersionPdf(sectionId, v.id, projectId, projectName)}
              title="Open this version as a PDF" style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b', padding: 0, marginTop: 1, fontSize: 11, fontWeight: 600, fontFamily: '"DM Mono", monospace' }}>
              PDF
            </button>
          </div>
        )
      })}
    </div>
  )
}

// One expected submittal (a register item) + its fulfillment status. When an actual submittal has
// been received against it, the node expands to that submittal (full review detail). This is the
// merged "expected + status in one place" row.
function ExpectedSubmittalNode({ item: it, fulfilling, projectId, onChanged }) {
  const menu = useActionMenu()
  const collapsed = wf.collapsed['spec-reg-item:' + it.id] !== false
  const received = !!fulfilling || !!it.fulfilled_by_submittal_id
  return (
    <div className={`wf-node wf-node--item${collapsed ? ' wf-node--collapsed' : ''}`} data-key={'spec-reg-item:' + it.id}>
      <div className="wf-row">
        {fulfilling
          ? <button className="wf-triangle" title={collapsed ? 'Expand' : 'Collapse'}
              onClick={() => { wf.collapsed['spec-reg-item:' + it.id] = !collapsed; window.dispatchEvent(new Event('outline-render')) }}>
              <span className={'wf-tri-icon ' + (collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down')} />
            </button>
          : <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />}
        <span className="wf-bullet wf-bullet--item" />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{it.type || 'Submittal'}{it.description ? ' — ' + it.description : ''}</span>
          <span className="wf-meta" style={{ marginLeft: 8, fontSize: 11, color: REGISTER_STATUS_COLOR[it.status] || '#9bb2b7' }}>
            {REGISTER_ITEM_STATUS_LABELS[it.status] || it.status}
          </span>
          {it.required_by ? <span className="wf-meta" style={{ marginLeft: 6, fontFamily: '"DM Mono",monospace', fontSize: 11 }}>due {fmtDate(it.required_by)}</span> : null}
          <span className="wf-meta" style={{ marginLeft: 6, fontSize: 11, color: received ? '#1D9E75' : '#c79a3a' }}>{received ? '✓ Received' : 'Awaiting'}</span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={async () => { await deleteRegisterItem(it.id); onChanged() }} danger>Remove expected submittal</ActionMenuItem>
      </ActionMenu>
      {fulfilling && !collapsed && (
        <div className="wf-children">
          <SubmittalNode submittal={fulfilling} contract={{ id: fulfilling.contract_id, project_id: fulfilling.project_id || projectId }} onChanged={onChanged} hideThreadMount mountCtx="spec" />
        </div>
      )}
    </div>
  )
}

// Merged Submittals branch: the EXPECTED submittals (register) with their status, plus any actual
// submittals received but not pre-registered. One place to see what's expected and where each stands.
function SpecSectionSubmittals({ sectionId, projectId, onChanged }) {
  const collapsed = wf.collapsed['spec-sub:' + sectionId] !== false
  const [expected, setExpected] = useState(undefined)   // register items (expected submittals)
  const [subs, setSubs] = useState(undefined)           // actual submittal records linked to the section
  const hasLoaded = useRef(false)
  const menu = useActionMenu()

  function load() {
    Promise.all([
      getSpecSectionRegisterItems(sectionId).catch(() => []),
      getSpecSectionSubmittals(sectionId).catch(() => []),
    ]).then(([reg, s]) => { setExpected(reg); setSubs(s); window.dispatchEvent(new Event('outline-render')) })
  }
  useEffect(() => {
    if (collapsed || hasLoaded.current) return
    hasLoaded.current = true
    load()
  }, [collapsed, sectionId])
  function reload() { hasLoaded.current = false; load(); onChanged && onChanged() }

  async function addItem() {
    const type = await promptDialog({
      title: 'Add expected submittal',
      message: 'Type — Shop Drawings, Product Data, Sample, or Other.',
      placeholder: 'Shop Drawings', defaultValue: 'Shop Drawings', confirmLabel: 'Next', required: true,
    })
    if (!type) return
    const desc = await promptDialog({
      title: 'Add expected submittal',
      message: 'Description — what specifically is required.',
      placeholder: 'e.g. Steel window/door shop drawings', confirmLabel: 'Add item', multiline: true,
    })
    if (desc === null) return
    await createRegisterItem(sectionId, projectId, { type: type.trim(), description: desc ? desc.trim() : null })
    reload()
  }
  function setAll(exp) {
    if (Array.isArray(expected)) expected.forEach(it => { wf.collapsed['spec-reg-item:' + it.id] = !exp })
    window.dispatchEvent(new Event('outline-render'))
  }

  // Correlate received submittals to the expected items they fulfill; the rest are "unregistered".
  const subsById = {}
  if (Array.isArray(subs)) for (const s of subs) subsById[s.id] = s
  const fulfilledIds = new Set((Array.isArray(expected) ? expected : []).map(it => it.fulfilled_by_submittal_id).filter(Boolean))
  const unmatched = (Array.isArray(subs) ? subs : []).filter(s => !fulfilledIds.has(s.id))
  const expectedCount = Array.isArray(expected) ? expected.length : 0
  const loading = expected === undefined || subs === undefined

  return (
    <div className="wf-node wf-node--section" data-key={'spec-sub:' + sectionId}>
      <div className="wf-row">
        <button className="wf-triangle" title={collapsed ? 'Expand' : 'Collapse'}
          onClick={() => { wf.collapsed['spec-sub:' + sectionId] = !collapsed; window.dispatchEvent(new Event('outline-render')) }}>
          <span className={'wf-tri-icon ' + (collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down')} />
        </button>
        <span className="wf-bullet wf-bullet--item" />
        <div className="wf-text-wrap">
          <span className="wf-node-text">Submittals</span>
          {expectedCount > 0 && <span className="wf-meta" style={{ marginLeft: 8, fontFamily: '"DM Mono",monospace', fontSize: 11 }}>{expectedCount} expected</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={addItem}>Add Expected Submittal</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => setAll(true)}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={() => setAll(false)}>Collapse all</ActionMenuItem>
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          {loading && loadingRow('Loading submittals…')}
          {!loading && expectedCount === 0 && unmatched.length === 0 && loadingRow('No submittals expected. Use ⋯ to add an expected submittal.')}
          {Array.isArray(expected) && expected.map(it => (
            <ExpectedSubmittalNode key={it.id} item={it}
              fulfilling={it.fulfilled_by_submittal_id ? subsById[it.fulfilled_by_submittal_id] : null}
              projectId={projectId} onChanged={reload} />
          ))}
          {unmatched.map(s => (
            <SubmittalNode key={s.id} submittal={s} contract={{ id: s.contract_id, project_id: s.project_id || projectId }} onChanged={reload} hideThreadMount mountCtx="spec" />
          ))}
        </div>
      )}
    </div>
  )
}

function SpecSectionNode({ section, projectId, onChanged }) {
  const collapsed = wf.collapsed['spec-sec:' + section.id] !== false
  const verCollapsed = wf.collapsed['spec-ver:' + section.id] !== false
  const menu = useActionMenu()
  const [assigning, setAssigning] = useState(false)   // inline milestone picker for an orphan section

  // Round 18 — a section with no version is an orphan: not assigned to any milestone, so it can't be
  // opened/edited until attached. The version chain is loaded with the section (`section.versions`).
  const orphan = !(section.versions && section.versions.length)
  // Spec-bearing milestones this project has (the assign targets).
  const specMilestones = (ctx.state?.milestones || [])
    .map(m => ({ id: m.id, name: (m.fields?.['Full Deliverable Name']) || (m.fields?.['Name (from Deliverable)'] || [])[0] || 'Milestone',
                 typeId: (m.fields?.['Deliverable'] || [])[0] || null }))
    .filter(m => Object.prototype.hasOwnProperty.call(SPEC_RELEASE_MS_TYPE_IDS, m.typeId))

  async function del() {
    if (!(await confirmDialog({ title: 'Delete spec section?', message: 'Delete spec section ' + section.section_number + '? This also deletes its versions and register items.', confirmLabel: 'Delete section', danger: true }))) return
    await deleteSpecSection(section.id)
    onChanged && onChanged()
  }
  async function assignTo(ms) {
    try { await addSectionToMilestone(section.id, { id: ms.id, name: ms.name }, 'Original Release'); setAssigning(false); onChanged && onChanged() }
    catch (e) { alertDialog({ title: 'Assign failed', message: e.message || String(e) }) }
  }

  return (
    <div className="wf-node wf-node--item" data-key={'spec-sec:' + section.id}>
      <div className="wf-row">
        <button className="wf-triangle" title={collapsed ? 'Expand' : 'Collapse'}
          onClick={() => { wf.collapsed['spec-sec:' + section.id] = !collapsed; window.dispatchEvent(new Event('outline-render')) }}>
          <span className={'wf-tri-icon ' + (collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down')} />
        </button>
        <button className="wf-bullet wf-bullet--item" title="Zoom into spec section" data-kbzoom="1"
          onClick={() => ctx.zoomToSection('spec-section-detail:' + section.id, { root: 'production' })} />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{section.section_number} — {section.section_name}</span>
          {orphan
            ? <span className="wf-meta" style={{ marginLeft: 8, fontSize: 10, color: '#c79a3a', fontFamily: '"DM Sans",sans-serif', fontWeight: 600 }} title="Not assigned to any milestone — assign it to a milestone to edit/version it">⚠ Not assigned to a milestone</span>
            : <span className="wf-meta" style={{ marginLeft: 8, fontSize: 10, color: 'var(--aj-text-muted)' }}>{section.versions.length} version{section.versions.length !== 1 ? 's' : ''}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => setAssigning(a => !a)}>Assign to milestone…</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={del} danger>Delete Section</ActionMenuItem>
      </ActionMenu>
      {assigning && (
        <div className="wf-children">
          {specMilestones.length === 0 && (
            <div className="wf-node wf-node--item"><div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden" /><span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }} />
              <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No spec-bearing milestones on this project.</span></div>
            </div></div>
          )}
          {specMilestones.map(ms => (
            <div key={ms.id} className="wf-node wf-node--item">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden" /><span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
                <span className="wf-bullet wf-bullet--item" />
                <div className="wf-text-wrap">
                  <button onClick={() => assignTo(ms)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#2563eb' }}>{ms.name}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {!collapsed && (
        <div className="wf-children">
          {/* Revisions — the version history (mirrors Drawing Sheets). The section body itself is not
              shown here; it lives in the milestone-scoped spec editor. */}
          <div className="wf-node wf-node--section" data-key={'spec-ver:' + section.id}>
            <div className="wf-row">
              <button className="wf-triangle" title={verCollapsed ? 'Expand' : 'Collapse'}
                onClick={() => { wf.collapsed['spec-ver:' + section.id] = !verCollapsed; window.dispatchEvent(new Event('outline-render')) }}>
                <span className={'wf-tri-icon ' + (verCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down')} />
              </button>
              <span className="wf-bullet wf-bullet--item" />
              <div className="wf-text-wrap"><span className="wf-node-text">Revisions</span>
                {!orphan && <span className="wf-meta" style={{ marginLeft: 8, fontFamily: '"DM Mono",monospace', fontSize: 11 }}>{section.versions.length}</span>}
              </div>
            </div>
            <SpecSectionVersionList sectionId={section.id} section={section} projectId={projectId} />
          </div>
          {/* Submittals — expected (register) + their received status, in one place */}
          <SpecSectionSubmittals sectionId={section.id} projectId={projectId} onChanged={onChanged} />
        </div>
      )}
    </div>
  )
}

/** Resolve the approved concept id from ctx.conceptCache (Selected Concept = true). */
function approvedConceptIdFromCtx() {
  const cache = ctx.conceptCache
  if (!Array.isArray(cache)) return null
  const found = cache.find(c => c.fields && c.fields['Selected Concept'] === true)
  return found ? found.id : null
}

/**
 * Resolve the approved concept id, self-loading ctx.conceptCache if nothing has populated it yet.
 *
 * Bug fixed here: approvedConceptIdFromCtx() only ever READS the cache — it returns null whenever
 * ctx.conceptCache is still its unset sentinel (null), which is the normal state until something
 * else (Design > Concepts, or a concept-branch milestone) has been opened in this session. That
 * made "Open AlderSPEC" / "Export combined PDF" report "No approved concept" even with a real
 * Active concept, whenever a user reached Production > Specifications without visiting Design
 * first. This tool's own entry point must not depend on another branch's cache having loaded.
 */
async function ensureConceptId(projectId, conceptIdProp) {
  if (conceptIdProp) return conceptIdProp
  if (Array.isArray(ctx.conceptCache)) return approvedConceptIdFromCtx()
  if (!projectId) return null
  try {
    const list = await getConcepts(projectId)
    ctx.conceptCache = list
    window.dispatchEvent(new Event('outline-render'))
    return approvedConceptIdFromCtx()
  } catch {
    return null
  }
}

export function SpecSectionsSubSection({ projectId, reload, conceptId: conceptIdProp, sectionKey = 'sec:prod:specs', label = 'Specifications', zoomed = false }) {
  const key = sectionKey
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  const [sections, setSections] = useState(undefined)
  const hasLoaded = useRef(false)
  // The approved concept — prefer the prop (DesignSection's authoritative value),
  // fall back to the outline engine's conceptCache.
  const [conceptId, setConceptId] = useState(() => conceptIdProp || approvedConceptIdFromCtx())

  useEffect(() => { if (conceptIdProp) setConceptId(conceptIdProp) }, [conceptIdProp])

  useEffect(() => {
    if (collapsed || hasLoaded.current || !projectId) return
    hasLoaded.current = true
    // Self-load the concept cache if nothing else has (see ensureConceptId above) before deciding
    // which concept's sections to query.
    ensureConceptId(projectId, conceptIdProp).then(cid => {
      setConceptId(cid)
      if (ctx.specSectionCache !== undefined) { setSections(ctx.specSectionCache); return }
      ctx.specSectionCache = null
      // Round 18 — load sections WITH their version chain so orphans (no milestone version) can be flagged.
      getSpecSectionsWithVersions(projectId, cid || null).then(s => {
        ctx.specSectionCache = s
        setSections(s)
        window.dispatchEvent(new Event('outline-render'))
      }).catch(() => { ctx.specSectionCache = []; setSections([]) })
    })
  }, [collapsed, projectId])

  // Also read from ctx cache on subsequent renders
  useEffect(() => {
    if (!collapsed && ctx.specSectionCache !== undefined && ctx.specSectionCache !== null) {
      setSections(ctx.specSectionCache)
    }
  }) // eslint-disable-line react-hooks/exhaustive-deps

  function onChanged() {
    ctx.specSectionCache = undefined; hasLoaded.current = false
    const cid = conceptIdProp || approvedConceptIdFromCtx()
    getSpecSectionsWithVersions(projectId, cid || null).then(s => {
      ctx.specSectionCache = s; setSections(s); window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {})
  }

  function setAll(exp) {
    if (Array.isArray(sections)) sections.forEach(s => { wf.collapsed['spec-sec:' + s.id] = !exp }); window.dispatchEvent(new Event('outline-render'))
  }

  async function openEditor() {
    // Pre-open the tab SYNCHRONOUSLY (inside the click) so popup blockers don't kill it after the
    // concept self-load below awaits — same trick SpecEditor.doExport uses for its PDF preview tab.
    const win = window.open('', '_blank')
    const cid = await ensureConceptId(projectId, conceptIdProp)
    if (cid) setConceptId(cid)
    if (!cid) {
      if (win && !win.closed) win.close()
      alertDialog({ title: 'No approved concept', message: 'A concept must be approved (Selected Concept = true on Project Concepts) before AlderSPEC can open.' })
      return
    }
    // AlderSPEC Phase 1 — the tool is its own tab now (/spec, the /modeler pattern). The overlay
    // editor remains reachable from milestone entry points until Phase 2's Releases tab lands.
    const url = '/spec?projectId=' + encodeURIComponent(projectId)
      + '&conceptId=' + encodeURIComponent(cid)
      + (ctx.projectName ? '&projectName=' + encodeURIComponent(ctx.projectName) : '')
    if (win && !win.closed) win.location.href = url
    else window.open(url, '_blank')
  }

  async function exportPdf() {
    const cid = await ensureConceptId(projectId, conceptIdProp)
    if (cid) setConceptId(cid)
    if (!cid) { alertDialog({ title: 'No approved concept', message: 'Approve a concept first.' }); return }
    import('../services/specifications.js')
      .then(m => m.exportSpecPdf(projectId, cid, ctx.projectName || ''))
      .catch(e => alertDialog({ title: 'PDF export failed', message: e.message || String(e) }))
  }

  const childrenContent = (
    <>
      {/* Concept gate — must have an approved concept */}
      {!conceptId && (
        <div className="wf-node wf-node--item">
          <div className="wf-row">
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
            <span className="wf-bullet wf-bullet--item" />
            <div className="wf-text-wrap">
              <span className="wf-node-text" style={{ color: '#c79a3a', fontStyle: 'italic', fontSize: 12 }}>
                A concept must be approved (Selected Concept) before spec sections can be managed.
              </span>
            </div>
          </div>
        </div>
      )}
      {conceptId && (sections === null || sections === undefined) && loadingRow('Loading…')}
      {conceptId && Array.isArray(sections) && sections.length === 0 && loadingRow('No spec sections yet. Use ⋯ → Open AlderSPEC to write the working spec from your answers.')}
      {conceptId && Array.isArray(sections) && sections.map(s => <SpecSectionNode key={s.id} section={s} projectId={projectId} onChanged={onChanged} />)}
    </>
  )

  // Section-zoom mode: render only the children (ZoomTitle shows the section name).
  if (zoomed) return <div className="wf-list">{childrenContent}</div>

  return (
    <div className="wf-node wf-node--section" data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" title={collapsed ? 'Expand' : 'Collapse'}
          onClick={() => { wf.collapsed[key] = !collapsed; window.dispatchEvent(new Event('outline-render')) }}>
          <span className={'wf-tri-icon ' + (collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down')} />
        </button>
        <button className="wf-bullet wf-bullet--item" title="Zoom into Specifications" data-kbzoom="1"
          onClick={() => ctx.zoomToSection(key, { root: 'production' })} />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{label}</span>
          {Array.isArray(sections) && sections.length > 0
            ? <span className="wf-meta" style={{ marginLeft: 8, fontFamily: '"DM Mono",monospace', fontSize: 11 }}>{sections.length}</span>
            : null}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        {/* AlderSPEC Phase 1 — this branch now opens the tool directly (previously openEditor()
            was defined but never wired to a menu item here — a pre-existing bug since Round 12.5,
            not something Phase 1 introduced; Round 18's comment describing this as "read-only" was
            never actually true in a way anyone could act on, since there was no edit entry at all). */}
        <ActionMenuItem onClick={openEditor}>Open AlderSPEC</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={exportPdf}>Export combined PDF</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => setAll(true)}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={() => setAll(false)}>Collapse all</ActionMenuItem>
      </ActionMenu>
      {!collapsed && <div className="wf-children">{childrenContent}</div>}
    </div>
  )
}

/* ── Main component ─────────────────────────────────────────────────────── */

export default function ConstructionSection() {
  const [, forceUpdate]         = useReducer(v => v + 1, 0)
  const [vis, setVis]           = useState(calcConstructionVis)
  const [recordId, setRecordId] = useState(() => wf.zoomProjectId || null)

  function ensureLoaded(projId) {
    if (!projId) return
    if (ctx.bidCampaignCache === undefined) {
      ctx.bidCampaignCache = null
      getProjectCampaigns(projId).then(c => {
        ctx.bidCampaignCache = c
        window.dispatchEvent(new Event('outline-render'))
      }).catch(() => {
        ctx.bidCampaignCache = []
        window.dispatchEvent(new Event('outline-render'))
      })
    }
    if (ctx.veEffortCache === undefined) {
      ctx.veEffortCache = null
      getProjectEfforts(projId).then(e => {
        ctx.veEffortCache = e
        window.dispatchEvent(new Event('outline-render'))
      }).catch(() => {
        ctx.veEffortCache = []
        window.dispatchEvent(new Event('outline-render'))
      })
    }
    if (ctx.contractCache === undefined) {
      ctx.contractCache = null
      getProjectContracts(projId).then(c => {
        ctx.contractCache = c
        window.dispatchEvent(new Event('outline-render'))
      }).catch(() => {
        ctx.contractCache = []
        window.dispatchEvent(new Event('outline-render'))
      })
    }
  }
  function reload() { ctx.bidCampaignCache = undefined; ctx.veEffortCache = undefined; ctx.contractCache = undefined; ensureLoaded(recordId) }

  useEffect(() => {
    if (!recordId) return
    ctx.bidCampaignCache = undefined
    ctx.bidCampaignDetailCache = {}
    ctx.bidLineItemCache = {}
    ctx.veEffortCache = undefined
    ctx.contractCache = undefined
    ctx.rfiCache = {}
    ctx.submittalCache = {}
    ctx.asiCache = {}
    ctx.caThreadCache = {}
    ctx.caZoomCache = {}
    ctx.proposalRequestCache = {}
    ctx.specSectionCache = undefined
    ensureLoaded(recordId)
  }, [recordId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function onRender() {
      const newRecordId = wf.zoomProjectId || null
      setRecordId(prev => (prev !== newRecordId ? newRecordId : prev))
      const v = calcConstructionVis()
      setVis(v)
      if (v !== 'hidden') ensureLoaded(newRecordId)
      forceUpdate()
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Targeted deep-link loader — when zoomed straight onto a CA entity (e.g. a hard refresh)
  // whose contract/campaign list cache was never built, fetch it by id into ctx.caZoomCache so
  // the zoom render, breadcrumb, and title resolve instead of hanging. zoomSection is the dep
  // (the component re-renders on every outline-render, so it stays current).
  const zoomSection = wf.zoomSection || ''
  useEffect(() => {
    const m = zoomSection.match(/^(rfi|submittal|asi|bid|spec-section|proposal-request)-detail:(.+)$/)
    if (!m) return
    const kind = m[1], id = m[2]
    if (!id) return
    if (!ctx.caZoomCache) ctx.caZoomCache = {}
    // Already resolvable from an existing cache? then no fetch needed.
    const z = ctx.caZoomCache[id]
    const inZoomCache = z && typeof z === 'object' && z.id === id
    let resolved = inZoomCache
    if (!resolved && kind === 'rfi') resolved = !!findCaItemById(ctx.rfiCache, id, 'rfi')
    else if (!resolved && kind === 'submittal') resolved = !!findCaItemById(ctx.submittalCache, id, 'submittal')
    else if (!resolved && kind === 'asi') resolved = !!findCaItemById(ctx.asiCache, id, 'asi')
    else if (!resolved && kind === 'bid') {
      const c = ctx.bidCampaignDetailCache || {}
      for (const cid of Object.keys(c)) { const d = c[cid]; if (d && Array.isArray(d.bids) && d.bids.some(b => b.id === id)) { resolved = true; break } }
    } else if (!resolved && kind === 'spec-section') {
      resolved = Array.isArray(ctx.specSectionCache) && ctx.specSectionCache.some(x => x.id === id)
    } else if (!resolved && kind === 'proposal-request') {
      const c = ctx.proposalRequestCache || {}
      for (const cid of Object.keys(c)) { const l = c[cid]; if (Array.isArray(l) && l.some(p => p.id === id)) { resolved = true; break } }
    }
    if (resolved) return
    if (ctx.caZoomCache[id] !== undefined) return // null (in-flight) or false (fetched, missing)
    ctx.caZoomCache[id] = null
    const fetcher = kind === 'rfi' ? getRfi(id)
      : kind === 'submittal' ? getSubmittal(id)
      : kind === 'asi' ? getAsi(id)
      : kind === 'bid' ? getBidDetail(id).then(d => d && d.bid)
      : kind === 'proposal-request' ? getProposalRequest(id).then(d => d && d.proposalRequest && { ...d.proposalRequest, responses: d.responses || [] })
      : getSpecSection(id)
    Promise.resolve(fetcher).then(rec => {
      ctx.caZoomCache[id] = rec || false
      if (rec && rec.project_id && ctx.contractCache === undefined) ensureLoaded(rec.project_id)
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {
      ctx.caZoomCache[id] = false
      window.dispatchEvent(new Event('outline-render'))
    })
  }, [zoomSection, recordId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (vis === 'hidden') return null

  const subSections = (
    <>
      <BidCampaignsSubSection projectId={recordId} reload={reload} />
      <VeEffortsSubSection reload={reload} />
      <ContractsSubSection reload={reload} />
    </>
  )

  // Bid zoom — children-only (ZoomTitle shows the bidder + normalized price)
  if (vis === 'bidZoom') {
    const bidId = (wf.zoomSection || '').split(':')[1] || ''
    const cache = ctx.bidCampaignDetailCache || {}
    let bid = null
    for (const cid of Object.keys(cache)) {
      const d = cache[cid]
      if (d && Array.isArray(d.bids)) {
        const found = d.bids.find(b => b.id === bidId)
        if (found) { bid = found; break }
      }
    }
    if (!bid) { const z = ctx.caZoomCache && ctx.caZoomCache[bidId]; if (z && typeof z === 'object') bid = z }
    if (!bid) return <div className="wf-list">{loadingRow(ctx.caZoomCache && ctx.caZoomCache[bidId] === false ? 'Bid not found.' : 'Loading bid…')}</div>
    return <div className="wf-list"><BidDetailRows bid={bid} /></div>
  }

  // Campaign zoom — children-only (ZoomTitle shows the campaign name + bid count)
  if (vis === 'campaignZoom') {
    const campId = (wf.zoomSection || '').split(':')[1] || ''
    const campaigns = ctx.bidCampaignCache
    if (campaigns === null || campaigns === undefined) return <div className="wf-list">{loadingRow('Loading…')}</div>
    const campaign = Array.isArray(campaigns) ? campaigns.find(c => c.id === campId) : null
    if (!campaign) return <div className="wf-list">{loadingRow('Campaign not found.')}</div>
    return (
      <div className="wf-list">
        <CampaignBody campaign={campaign} projectId={recordId} onChanged={reload} zoom />
      </div>
    )
  }

  // VE effort zoom — children-only (ZoomTitle shows the effort + accepted savings)
  if (vis === 'veEffortZoom') {
    const effId = (wf.zoomSection || '').split(':')[1] || ''
    const efforts = ctx.veEffortCache
    if (efforts === null || efforts === undefined) return <div className="wf-list">{loadingRow('Loading…')}</div>
    const effort = Array.isArray(efforts) ? efforts.find(e => e.id === effId) : null
    if (!effort) return <div className="wf-list">{loadingRow('Effort not found.')}</div>
    return <div className="wf-list"><VeEffortBody effort={effort} onChanged={reload} /></div>
  }

  // VE item zoom — children-only (ZoomTitle shows the item title + savings)
  if (vis === 'veItemZoom') {
    const itemId = (wf.zoomSection || '').split(':')[1] || ''
    const efforts = ctx.veEffortCache
    let item = null
    if (Array.isArray(efforts)) {
      for (const e of efforts) { const f = (e.ve_items || []).find(i => i.id === itemId); if (f) { item = f; break } }
    }
    if (!item) return <div className="wf-list">{loadingRow('Item not found — open its effort first.')}</div>
    return <div className="wf-list"><VeItemDetailRows item={item} /></div>
  }

  // Contract zoom — children-only (ZoomTitle shows the contractor + value)
  if (vis === 'contractZoom') {
    const cid = (wf.zoomSection || '').split(':')[1] || ''
    const contracts = ctx.contractCache
    if (contracts === null || contracts === undefined) return <div className="wf-list">{loadingRow('Loading…')}</div>
    const contract = Array.isArray(contracts) ? contracts.find(c => c.id === cid) : null
    if (!contract) return <div className="wf-list">{loadingRow('Contract not found.')}</div>
    return <div className="wf-list"><ContractBody contract={contract} onChanged={reload} /></div>
  }

  // RFI zoom — children-only (ZoomTitle shows the RFI # + subject)
  if (vis === 'rfiZoom') {
    const rfiId = (wf.zoomSection || '').split(':')[1] || ''
    const rfi = findCaItemById(ctx.rfiCache, rfiId, 'rfi')
    if (!rfi) return <div className="wf-list">{loadingRow(ctx.caZoomCache && ctx.caZoomCache[rfiId] === false ? 'RFI not found.' : 'Loading RFI…')}</div>
    return <div className="wf-list"><RfiDetailRows rfi={rfi} />{rfi.milestone_airtable_id
      ? <CaMilestoneMount milestoneId={rfi.milestone_airtable_id} projectId={rfi.project_id} />
      : <CaThreadMount threadId={rfi.thread_airtable_id} projectId={rfi.project_id} />}</div>
  }

  // Submittal zoom — children-only (ZoomTitle shows the spec section + status)
  if (vis === 'submittalZoom') {
    const sid = (wf.zoomSection || '').split(':')[1] || ''
    const s = findCaItemById(ctx.submittalCache, sid, 'submittal')
    if (!s) return <div className="wf-list">{loadingRow(ctx.caZoomCache && ctx.caZoomCache[sid] === false ? 'Submittal not found.' : 'Loading submittal…')}</div>
    return <div className="wf-list"><SubmittalDetailRows submittal={s} />{s.milestone_airtable_id
      ? <CaMilestoneMount milestoneId={s.milestone_airtable_id} projectId={s.project_id} />
      : <CaThreadMount threadId={s.thread_airtable_id} projectId={s.project_id} />}</div>
  }

  // Spec section zoom — children-only (ZoomTitle shows section number + name)
  if (vis === 'specSectionZoom') {
    const sid = (wf.zoomSection || '').split(':')[1] || ''
    const sections = ctx.specSectionCache
    let s = Array.isArray(sections) ? sections.find(x => x.id === sid) : null
    if (!s) { const z = ctx.caZoomCache && ctx.caZoomCache[sid]; if (z && typeof z === 'object') s = z }
    if (!s) return <div className="wf-list">{loadingRow(ctx.caZoomCache && ctx.caZoomCache[sid] === false ? 'Spec section not found.' : 'Loading spec section…')}</div>
    return (
      <div className="wf-list">
        <SpecSectionNode section={s} projectId={recordId} onChanged={() => { ctx.specSectionCache = undefined; window.dispatchEvent(new Event('outline-render')) }} />
      </div>
    )
  }

  // ASI zoom — children-only (ZoomTitle shows the ASI number + subject)
  if (vis === 'asiZoom') {
    const aid = (wf.zoomSection || '').split(':')[1] || ''
    const a = findCaItemById(ctx.asiCache, aid, 'asi')
    if (!a) return <div className="wf-list">{loadingRow(ctx.caZoomCache && ctx.caZoomCache[aid] === false ? 'ASI not found.' : 'Loading ASI…')}</div>
    const editable = a.status !== 'issued'
    async function saveAsiZoomField(fields) {
      try {
        const updated = await updateAsi(a.id, fields)
        if (ctx.asiCache && a.contract_id) {
          const cached = ctx.asiCache[a.contract_id]
          if (Array.isArray(cached)) { const idx = cached.findIndex(x => x.id === a.id); if (idx !== -1) cached[idx] = updated }
        }
        window.dispatchEvent(new Event('outline-render'))
      } catch (e) { alertDialog({ title: 'Save failed', message: e.message || String(e) }) }
    }
    return <div className="wf-list"><AsiDetailRows asi={a} editable={editable} onSave={saveAsiZoomField} />{a.milestone_airtable_id
      ? <CaMilestoneMount milestoneId={a.milestone_airtable_id} projectId={a.project_id} />
      : <CaThreadMount threadId={a.thread_airtable_id} projectId={a.project_id} />}</div>
  }

  // Proposal Request zoom — children-only (ZoomTitle shows the PR # + subject)
  if (vis === 'prZoom') {
    const prId = (wf.zoomSection || '').split(':')[1] || ''
    let pr = null
    const cache = ctx.proposalRequestCache || {}
    for (const cid of Object.keys(cache)) { const l = cache[cid]; if (Array.isArray(l)) { const f = l.find(p => p.id === prId); if (f) { pr = f; break } } }
    if (!pr) { const zc = ctx.caZoomCache && ctx.caZoomCache[prId]; if (zc && typeof zc === 'object') pr = zc }
    if (!pr) return <div className="wf-list">{loadingRow(ctx.caZoomCache && ctx.caZoomCache[prId] === false ? 'Proposal request not found.' : 'Loading proposal request…')}</div>
    return <div className="wf-list"><ProposalRequestBody pr={pr} /></div>
  }

  // Per-contract sub-section zoom — children-only (ZoomTitle shows the sub-section name + count).
  // Key shape: sec:contract:<contractId>:<type> where type ∈ rfis|submittals|asis|prs.
  if (vis === 'contractSectionZoom') {
    const rest = (wf.zoomSection || '').slice('sec:contract:'.length)
    const lc = rest.lastIndexOf(':')
    const ci = rest.slice(0, lc)
    const type = rest.slice(lc + 1)
    const contracts = ctx.contractCache
    if (contracts === null || contracts === undefined) return <div className="wf-list">{loadingRow('Loading…')}</div>
    const contract = Array.isArray(contracts) ? contracts.find(c => c.id === ci) : null
    if (!contract) return <div className="wf-list">{loadingRow('Contract not found.')}</div>
    let content
    if (type === 'rfis') content = <RfiList contract={contract} onChanged={reload} zoom />
    else if (type === 'submittals') content = <SubmittalList contract={contract} onChanged={reload} zoom />
    else if (type === 'asis') content = <AsiList contract={contract} onChanged={reload} zoom />
    else if (type === 'prs') content = <ProposalRequestList contract={contract} onChanged={reload} zoom />
    else content = loadingRow('Coming soon')
    return <div className="wf-list">{content}</div>
  }

  // Section zoom — children-only (ZoomTitle shows the section name + count)
  if (vis === 'sectionZoom') {
    const t = (wf.zoomSection || '').slice('sec:construction:'.length)
    if (t === 'specs') return <div className="wf-list"><SpecSectionsSubSection projectId={recordId} reload={reload} /></div>
    if (t === 'bid') return <div className="wf-list"><BidCampaignsContent projectId={recordId} reload={reload} /></div>
    if (t === 've')  return <div className="wf-list"><VeEffortsContent reload={reload} /></div>
    if (t === 'contracts') return <div className="wf-list"><ContractsContent reload={reload} /></div>
    return <div className="wf-list">{loadingRow('Coming soon')}</div>
  }

  if (vis === 'rootZoom') return <div className="wf-list">{subSections}</div>

  // Reference child — at project level the root row renders only when the
  // Reference node is expanded (indented); in Reference zoom it renders plain.
  if (vis === 'home' && wf.collapsed['root:reference'] !== false) return null

  const rootCollapsed = wf.collapsed['root:construction'] !== false
  const rootNode = (
      <div
        className={`wf-node wf-node--root wf-node--root-construction${rootCollapsed ? ' wf-node--collapsed' : ''}`}
        data-key="root:construction"
      >
        <div className="wf-row">
          <button
            className="wf-triangle"
            onClick={e => {
              e.stopPropagation()
              const next = !rootCollapsed
              wf.collapsed['root:construction'] = next
              saveViewState()
              if (!next) ensureLoaded(recordId)
              window.dispatchEvent(new Event('outline-render'))
            }}
          >
            <span className={`wf-tri-icon ${rootCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
          </button>
          <button
            className="wf-bullet wf-bullet--root"
            title="Focus on Construction"
            data-fn="zoomTo"
            data-level="root"
            data-rootname="construction"
          ></button>
          <div className="wf-text-wrap">
            <span className="wf-node-text wf-node-text--root">Construction</span>
          </div>
          <button
            className="wf-menu-btn"
            data-fn="openMenu"
            data-type="root"
            data-key="root:construction"
            data-rootname="construction"
          >&#8943;</button>
        </div>
        {!rootCollapsed && <div className="wf-children">{subSections}</div>}
      </div>
  )

  if (vis === 'referenceZoom') return <div className="wf-list">{rootNode}</div>
  return <div className="wf-list"><div className="wf-ref-child">{rootNode}</div></div>
}
